import * as THREE from 'three';
import earcut from 'earcut';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../viewer/viewer';
import { CameraMode, DefaultColors } from '../defines';
import { SphereMarker } from './SphereMarker';
import { buildWallRegionFromRooms } from './WallRegionBuilder';
import { WallSketchSelection, SKELETON_SELECTION_COLOR } from '../personas/tools/modules/selection/WallSketchSelection';
import { WallSketchHover } from '../personas/tools/modules/selection/WallSketchHover';
import { PixelHitTester } from '../personas/tools/modules/selection/PixelHitTester';
import {
  getWallSketchOverlayVisibleProperty,
  getWallSketchSkinning2DColorProperty,
  getWallSketchSkinning2DOutlineColorProperty,
  getAnnotationColorProperty,
  getAnnotationVisibleProperty,
  getWallSketchNodesProperty,
  getWallSketchEdgesProperty,
  getWallSketchMetadataProperty,
  getWallSketchWallRegionsProperty,
  getOpeningWallSketchEdgeIdProperty,
  getFloorMesh3DEnabledProperty,
  getFloorMesh3DOpacityProperty,
  getFloorWallSourceProperty,
  getFloorHeatmapEnabledProperty,
  getFloorHeatmapGradientProperty,
  getFloorHeatmapMaxDistanceProperty,
  getFloorHeatmapClipEnabledProperty,
  getFloorHeatmapClipMethodProperty,
  getFloorHeatmapClipToleranceProperty,
  getOutlineOuterRingProperty,
  getOutlineCavitiesProperty,
  getRoomPolygonProperty,
  Subscription,
} from '@pointorama/potree-shared';
import { OpeningAnnotation } from './PointoramaAnnotation';
import type { SkeletonNodeData, SkeletonEdgeData, SkeletonWallType, WallRegionData } from '@pointorama/potree-shared';
import {
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchLowerBoundProperty,
  getOpeningSketchHeightProperty,
  getOpeningSketchInsetProperty,
  getOpeningSketchKindProperty,
  getOpeningSketchDoorTypeProperty,
  getOpeningSketchHingeAtStartProperty,
  getOpeningSketchSwingPositiveProperty,
  getOpeningSketchRollableProperty,
  getOpeningSketchWindowTypeProperty,
} from '@pointorama/potree-shared';
import { OpeningKind, type DoorGlyphType, type ObjectParamValue, type WindowGlyphType } from 'shared-types';
import { getCustomOpeningType, onCustomOpeningTypesChanged } from '../sketchObjects/custom/openings/customOpeningRegistry';
import { specToParamSchema } from '../sketchObjects/custom/customSketchObjectType';
import { OPENING_PARAM_SCOPE, readParamValues } from '../sketchObjects/paramSchema';
import { FloorHeightInfo, resolveSkeletonConstraint, FALLBACK_WALL_HEIGHT } from './skeleton/resolveSkeletonHeight';
import { buildVerticalPrismCutter, frameFromWallOpeningSketch, signedPerpFromInset, OPENING_INFLATE, type OpeningFrame } from './FloorMesh3DBuilder';
import { buildOpeningGlyph, customGlyphTypeOf, type OpeningGlyphInput } from './skeleton/openingGlyphs';
import { computeCreaseEdges, extractAllTriangleEdges } from './creaseEdges';
import { SkeletonFixture, summarizeWallGeometry } from './skeleton/skeletonFixture';
import { WallRegion, buildStitchedWallMesh } from './skeleton/stitchedWallMesh';
import { computeSdfWallRegions } from './skeleton/sdfPartition';
import { tessellateEdge, tessellateBand, tessellateOffset, subSpan, arcMidpoint, edgeIsCurved, pointAtArcLength, edgeLength as arcEdgeLength, arcFromBulge, refitArcsToRing, type ArcCandidate } from './skeleton/arcGeometry';
import { subtractFromRegions, regionsToFootprint2D, onManifoldReady, isManifoldReady, TaggedCutter, FootprintPolygon } from './manifoldCsg';
import { collectOpenOutlineEdgeIds, extractOutlineRings } from './skeleton/outlineRings';
import { deriveOutlineRingsFromRooms } from './skeleton/roomsOutlineFallback';
import { applyBackdropInvert } from './BackdropContrast';
export type { SkeletonNodeData, SkeletonEdgeData, SkeletonWallType, WallRegionData };

/** One measured wall face found near a sketch edge: its perpendicular
 *  distance from the centerline plus the face's own LINE (point + unit
 *  direction oriented along the wall axis) — measured faces need not be
 *  parallel to the sketch edge. */
export interface MeasuredWallFaceHit {
  distance: number;
  point: { x: number; y: number };
  dir: { x: number; y: number };
}
// Note: SDF texture approach replaced with per-edge distance calculation in shader

/**
 * PointoramaWallSketch represents a skeleton graph for a floor:
 * - Nodes at wall intersections
 * - Edges connecting nodes along wall centerlines
 *
 * Uses same rendering approach as PointoramaRoom:
 * - Line2 with LineMaterial for edges (single geometry for all edges)
 * - SphereMarker for nodes
 * - Compatible with EDLRenderer depth fade system
 */
/**
 * Render order for the skeleton's node markers (the draggable / hoverable node
 * spheres). The 2D/3D wall skinning meshes render at 1–5; the node markers must
 * sit above them so handles and hover indication are never hidden behind the
 * wall mesh. Below the editor-session helper visuals (≈999) so those still win.
 */
const NODE_MARKER_RENDER_ORDER = 10;

/** Edge data with computed offset lines for skinning */
interface EdgeOffsetData {
  edge: SkeletonEdgeData;
  startNode: SkeletonNodeData;
  endNode: SkeletonNodeData;
  /** Unit CHORD direction start → end. */
  direction: THREE.Vector2;
  perpLeft: THREE.Vector2;
  /**
   * Unit TRAVEL tangents at each endpoint — equal to `direction` for straight
   * edges, local circle tangents for arcs. Junction miters and cap corners
   * are computed against these so arcs join neighbours along their tangent.
   */
  startTangent: THREE.Vector2;
  endTangent: THREE.Vector2;
  leftSize: number;
  rightSize: number;
  /** Cap corners: endpoint ± the LOCAL tangent's perpendicular × size. */
  leftStart: THREE.Vector2;
  leftEnd: THREE.Vector2;
  rightStart: THREE.Vector2;
  rightEnd: THREE.Vector2;
}

/** Edge at a node with direction info for skinning */
interface EdgeAtNode {
  edgeId: string;
  offsetData: EdgeOffsetData;
  outgoingDir: THREE.Vector2;
  isStartNode: boolean;
  angle: number;
  cwWallLine: { p1: THREE.Vector2; p2: THREE.Vector2; size: number };
  ccwWallLine: { p1: THREE.Vector2; p2: THREE.Vector2; size: number };
}

export class PointoramaWallSketch extends THREE.Object3D {
  private _identifier = '';
  groupIdentifier: string | null = null;
  finished = false;
  selected = false;
  boundingSphere?: THREE.Sphere;

  // Identifier getter/setter to trigger reactive property loading
  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    // Load reactive properties when identifier is set
    if (value) {
      this.loadReactiveProperties();
    }
  }

  private nodes: Map<string, SkeletonNodeData> = new Map();
  private edges: Map<string, SkeletonEdgeData> = new Map();
  private metadata: string | null = null;
  /**
   * Persisted wall-region polygons (outer wall outlines + cavities) — derived
   * from rooms via boolean union, but updated only when the user explicitly
   * triggers a recompute. WallSketch edits do NOT touch this; it only changes
   * when {@link recomputeWallRegions} is called.
   */
  private wallRegions: WallRegionData[] = [];

  /**
   * All floor groups in the project (heights only), used to resolve per-node /
   * per-edge {@link SkeletonHeightConstraint}s into absolute top heights for 3D
   * extrusion. Pushed in by the editor tool; empty until then (the resolver
   * then falls back to this floor's ceiling / a fixed default).
   */
  private floorGroupsData: FloorHeightInfo[] = [];

  // Visual elements - using same patterns as PointoramaRoom
  // Exposed as public for EDLRenderer depth fade compatibility
  public spheres: SphereMarker[] = [];
  private nodeMarkerMap: Map<string, SphereMarker> = new Map();

  // Line elements - using LineSegments2 for discrete edge segments
  private lineGeometry: LineSegmentsGeometry;
  private line: LineSegments2;
  public lineMaterial: LineMaterial; // Public for depth fade updates
  public backgroundLine: LineSegments2; // Public for line intersection detection
  public backgroundLineMaterial: LineMaterial; // Public for depth fade updates
  // Measured-representation error overlay: red duplicates of the skeleton
  // segments that violate the measured geometry (outside the floor outline /
  // inside a room). Empty and hidden in the Wall-sketch representation.
  private errorLineGeometry: LineSegmentsGeometry;
  private errorLine: LineSegments2;
  private errorLineMaterial: LineMaterial;

  private nodeGroup: THREE.Group;
  private edgeGroup: THREE.Group;

  private color: THREE.Color;
  private viewer: Viewer;

  // Skinning state. 2D skinning is always on and rendered transparent
  // (opacity 0.4) with vertex colors carrying the fill — selection
  // highlights paint into that vertex-color buffer. Only the 3D toggle
  // remains user-configurable.
  private _skinningEnabled = true;
  private _skinning3D = false;
  // Visibility of the skeleton graph overlay (node markers + edge lines), driven
  // by the `skeleton_overlay_visible` reactive property.
  private _overlayVisible = true;
  /**
   * Debug flag — when true, the wall outline overlay shows EVERY triangle
   * edge in the extruded mesh (not just creases). Lets you inspect the raw
   * triangulation produced by Manifold's union/cut. Toggled via the
   * skeleton debug widget; off by default.
   */
  private static _showAllTriangles = false;
  static setShowAllTriangles(value: boolean): void {
    PointoramaWallSketch._showAllTriangles = value;
  }
  static getShowAllTriangles(): boolean {
    return PointoramaWallSketch._showAllTriangles;
  }
  /** Wall fill opacity for 2D skinning. */
  private static readonly SKINNING_2D_OPACITY = 0.4;
  /** Single wall color, used for both the 2D fill and the 3D extruded mesh. */
  private _skinning2DColor = '#808080';
  private _skinning2DOutlineColor = '#000000';
  /** 3D wall opacity — driven by the floor's `floor_mesh3d_opacity`. */
  private _skinning3DOpacity = 0.7;

  // Selection — owned by this skeleton; tools fetch via getSelection().
  // `_selection` is lazily created on first access so the rest of the
  // constructor can finish (the WallSketchSelection module imports back into
  // PointoramaWallSketch).
  private _selection: WallSketchSelection | null = null;
  private _selectionSubscribers: Set<() => void> = new Set();

  // Hover — owned by this skeleton; tools (editor, door/window) write
  // their hit-test result here instead of holding private `hoveredId`
  // fields. Subscribers (tooltip overlays, status bars) read from one
  // place. Lazily created for the same reason as `_selection`.
  private _hover: WallSketchHover | null = null;
  private _hoverSubscribers: Set<() => void> = new Set();
  /** Notified at the end of every `setData` — lets the React property panel
   * re-render from the live (possibly uncommitted) node/edge data. */
  private _dataSubscribers: Set<() => void> = new Set();

  // Skinning visual elements
  private skinningGroup: THREE.Group | null = null;
  /**
   * World-space reference the skinning group is localized around (set each
   * rebuild to the first node). Every skinning vertex is expressed relative to
   * it BEFORE it goes into a float32 buffer (see `toLocalFlat`, the localized
   * regions and cutters of the 3D build): float32 steps are ~0.5 m at UTM
   * northings, so absolute coords would make the wall mesh jitter far from the
   * origin, and shifting a float32 buffer afterwards cannot recover that.
   */
  private _skinningRef = new THREE.Vector3();
  // Unsubscribe for a pending "rebuild once Manifold WASM is ready" callback,
  // set when 3D skinning is built with openings before the WASM has loaded.
  private _manifoldReadyUnsub: (() => void) | null = null;
  // ORIGINAL-representation geometry subscriptions (outline ring/cavities +
  // room polygons) — resubscribed on every original-mode build, cleared with
  // the skin.
  private _originalGeomSubs: Subscription[] = [];
  /** Whether the current centerline build includes the merged outline
   *  subgraph (Measured representation only) — checked on representation
   *  switches to know when the centerline must rebuild. */
  private _outlineInCenterline = false;
  // Coalesces original-mode rebuilds triggered by outline/room edits.
  private _originalRebuildQueued = false;
  // Public so editor sessions can override material settings (e.g. transparent
  // wall fills during skeleton edit mode) without copying the rebuild logic.
  public wallMeshes: THREE.Mesh[] = [];
  private outlineLine: LineSegments2 | null = null;
  private outlineGeometry: LineSegmentsGeometry | null = null;
  private outlineMaterial: LineMaterial | null = null;

  // Door/window floor-plan glyphs (leaf + swing arc / glass lines) in the 2D skin.
  private glyphLine: LineSegments2 | null = null;
  private glyphGeometry: LineSegmentsGeometry | null = null;
  private glyphMaterial: LineMaterial | null = null;
  // Separate line for glyphs of SELECTED openings, drawn in the selection color.
  private selectedGlyphLine: LineSegments2 | null = null;
  private selectedGlyphGeometry: LineSegmentsGeometry | null = null;
  private selectedGlyphMaterial: LineMaterial | null = null;
  // Per-opening glyph line segments in world XY (identifier → flat [x,y,z,...]),
  // rebuilt each skinning pass and used for click hit-testing of the symbols.
  // Keyed by OPENING_SKETCH identifier. Glyph-param changes are observed by the
  // PointoramaOpeningSketch itself, which calls rebuildOpeningGlyphsOnly().
  private _openingGlyphSegments = new Map<string, number[]>();

  // Skinning constants
  private readonly OUTLINE_WIDTH = 3;
  private readonly COLLINEAR_THRESHOLD = 0.95;

  // Property subscriptions
  private _overlayVisibleSubscription: Subscription | null = null;
  private _skinning3DSubscription: Subscription | null = null;
  private _skinning2DColorSubscription: Subscription | null = null;
  private _skinning2DOutlineColorSubscription: Subscription | null = null;
  private _skinning3DOpacitySubscription: Subscription | null = null;

  // Edge distance state for point cloud coloring (per-edge calculation in shader)
  // Effective heatmap/clipping state of THIS pipeline. Driven by the floor's
  // consolidated `floor_heatmap_*` properties: effective only while the
  // floor's `floor_wall_source` is WALL_SKETCH (see loadReactiveProperties).
  private _sdfEnabled = false;
  private _sdfGradient = 'SPECTRAL';
  private _sdfMaxDistance = 5.0;
  private _sdfClipEnabled = false; // Master toggle for clipping
  private _sdfClipMethod = 'SHOW_INSIDE'; // SHOW_INSIDE, SHOW_OUTSIDE (only used when clip enabled)
  private _sdfClipTolerance = 1.0; // Distance for clip method
  // Raw floor-panel values + source, combined into the effective flags above.
  // The source also gates 3D skinning (`_skinning3D`) — the floor renders its
  // 3D as either these skeleton walls or the rooms-based floor mesh.
  private _floorHeatmapEnabled = false;
  private _floorHeatmapClipEnabled = false;
  private _floorMesh3DEnabledRaw = false;
  private _wallSourceIsWallSketch = false;
  private _edgeTexture: THREE.DataTexture | null = null;
  private _edgeCount = 0;
  private _sdfRegenerationPending = false;

  // SDF property subscriptions
  private _sdfEnabledSubscription: Subscription | null = null;
  private _sdfGradientSubscription: Subscription | null = null;
  private _sdfMaxDistanceSubscription: Subscription | null = null;
  private _sdfClipEnabledSubscription: Subscription | null = null;
  private _sdfClipMethodSubscription: Subscription | null = null;
  private _sdfClipToleranceSubscription: Subscription | null = null;
  private _wallSourceSubscription: Subscription | null = null;
  private _colorSubscription: Subscription | null = null;
  private _visibleSubscription: Subscription | null = null;
  private _nodesSubscription: Subscription | null = null;
  private _edgesSubscription: Subscription | null = null;
  private _metadataSubscription: Subscription | null = null;
  private _wallRegionsSubscription: Subscription | null = null;

  // Wall-region overlay (rendered above the skeleton edges in the same color
  // as the line). Lives in its OWN child group with its OWN position so it
  // doesn't interfere with `this.position` (which `updateVisualization` owns
  // as the reference point for the skeleton's line geometry). Built from
  // `wallRegions` whenever it changes; null when no regions have been
  // computed yet.
  private wallRegionGroup: THREE.Group | null = null;
  private wallRegionLine: LineSegments2 | null = null;
  private wallRegionGeometry: LineSegmentsGeometry | null = null;
  private wallRegionMaterial: LineMaterial | null = null;

  // Bound event handler for point cloud loading
  private _onPointCloudAdded: ((event: { type: string } & Record<string, unknown>) => void) | null = null;
  private _onSelectionChanged: ((event: { type: string } & Record<string, unknown>) => void) | null = null;
  /** Unsubscribe from organisation door / window type changes (library loads repaint the glyphs). */
  private _customOpeningTypesUnsub: (() => void) | null = null;

  constructor(
    viewer: Viewer,
    color?: THREE.Color,
    initialData?: {
      nodes: SkeletonNodeData[];
      edges: SkeletonEdgeData[];
      metadata?: string | null;
    },
  ) {
    super();

    this.viewer = viewer;
    this.color = color || DefaultColors.OBJECTS_THREE;

    // Create groups for organization
    this.nodeGroup = new THREE.Group();
    this.nodeGroup.name = 'skeleton_nodes';
    this.add(this.nodeGroup);

    this.edgeGroup = new THREE.Group();
    this.edgeGroup.name = 'skeleton_edges';
    this.add(this.edgeGroup);

    // Create line geometry and materials using LineSegments2 for discrete segments
    // Add lines directly to this object (like PointoramaRoom does)
    this.lineGeometry = new LineSegmentsGeometry();
    this.lineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 1,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
      transparent: true,
    });
    this.line = new LineSegments2(this.lineGeometry, this.lineMaterial);
    // Above the wall skinning meshes (1–5) so the edge centerlines — which
    // carry hover/selection tinting — are never hidden behind the wall mesh.
    this.line.renderOrder = 8;
    this.add(this.line);

    // Create background line for selection
    this.backgroundLineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 15,
      resolution: new THREE.Vector2(1000, 1000),
      transparent: true,
      depthTest: false,
      opacity: 0.0,
    });
    this.backgroundLine = new LineSegments2(this.lineGeometry, this.backgroundLineMaterial);
    this.backgroundLine.name = 'WallSketchBackgroundLine';
    // Selection halo — just under the main centerline (8) but still above the
    // wall meshes (1–5) so a selected edge's glow isn't swallowed by the mesh.
    this.backgroundLine.renderOrder = 7;
    this.add(this.backgroundLine);

    // Measured-view error overlay: red segments drawn OVER the centerline
    // where the skeleton violates the measured geometry. Slightly wider than
    // the centerline so the error reads at a glance.
    this.errorLineGeometry = new LineSegmentsGeometry();
    this.errorLineMaterial = new LineMaterial({
      color: new THREE.Color(0xff3333),
      linewidth: 3,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
      transparent: true,
    });
    this.errorLine = new LineSegments2(this.errorLineGeometry, this.errorLineMaterial);
    this.errorLine.name = 'WallSketchErrorLine';
    this.errorLine.renderOrder = 9; // above the centerline (8)
    this.errorLine.visible = false;
    this.add(this.errorLine);

    // Create skinning group - add to floorplanTool.scene for world coordinates
    this.skinningGroup = new THREE.Group();
    this.skinningGroup.name = 'skeleton_skinning';
    // Will be added to viewer.floorplanTool.scene when enabled

    // Listen for point cloud additions to apply SDF to newly loaded point clouds
    this._onPointCloudAdded = () => {
      if (this._sdfEnabled && this._edgeTexture) {
        this.updateSDFForPointClouds();
      }
    };
    this.viewer.scene.addEventListener('pointcloud_added', this._onPointCloudAdded);

    // Rebuild the 2D skin when the global selection changes, so a selected
    // opening's glyph repaints in the selection color (and clears when
    // deselected). Only the 2D path renders glyphs, so this is a no-op cost in
    // 3D mode beyond a cheap rebuild.
    this._onSelectionChanged = () => {
      // Only the glyph highlight changes on selection — the wall hole is
      // unchanged, so patch just the glyph lines, not the whole skin.
      this.rebuildOpeningGlyphsOnly();
    };
    this.viewer.scene.addEventListener('objects_selection_changed', this._onSelectionChanged);

    // Organisation doors / windows arrive with the library (or change when it
    // is republished): repaint the glyphs — the holes don't depend on them.
    this._customOpeningTypesUnsub = onCustomOpeningTypesChanged(() => this.rebuildOpeningGlyphsOnly());

    // If initial data provided, set it immediately
    if (initialData) {
      this.setData(initialData.nodes, initialData.edges, initialData.metadata);
    }

    // Note: geometry loading from properties (when no initialData) happens in loadReactiveProperties,
    // triggered when identifier is set via the setter.
  }

  /**
   * Get skinning 3D mode state
   */
  get skinning3D(): boolean {
    return this._skinning3D;
  }

  /**
   * Set skinning 3D mode state
   */
  set skinning3D(value: boolean) {
    if (this._skinning3D === value) return;
    this._skinning3D = value;
    this.updateSkinningVisualization();
  }

  /**
   * The 3D wall extrusion is a 3D-only EFFECT: when the main view is
   * orthographic (2D — X-Ray / plan / 2D-top), the sketch renders its flat 2D
   * skin regardless of the floor's `floor_mesh3d_enabled` property. The
   * property (and `_skinning3D`, which mirrors it) is untouched — switching
   * back to a 3D view restores the extrusion. Rebuilds are triggered by
   * `viewer.setCameraMode` on actual mode changes.
   */
  private get skinning3DEffective(): boolean {
    return this._skinning3D && this.viewer.scene.cameraMode !== CameraMode.ORTHOGRAPHIC;
  }

  /**
   * Get the selection for this skeleton (lazily created). Tools call this to
   * read or mutate selection state — selection ownership lives here so that
   * the skeleton's own visuals (skinning highlight + selection overlay) and
   * any tool listeners stay coherent.
   */
  getSelection(): WallSketchSelection {
    if (!this._selection) {
      this._selection = new WallSketchSelection({
        viewer: this.viewer,
        getWallSketch: () => this,
        deselectedNodeColor: () => this.lineMaterial.color.clone(),
        // highlightColor defaults to SKELETON_SELECTION_COLOR (brand green)
        // — same tint the skinning fill uses below for selected wall sections.
        onSelectionChanged: () => {
          // Repaint skinning fill so the selected wall sections highlight.
          this.updateSkinningVisualization();
          // Fan out to subscribers.
          for (const cb of this._selectionSubscribers) {
            try {
              cb();
            } catch (err) {
              console.error('WallSketch selection subscriber threw:', err);
            }
          }
        },
      });
    }
    return this._selection;
  }

  /**
   * The skinning group when it holds the 2D wall representation (flat fill,
   * outline, opening glyphs), or null when the skeleton is in 3D-mesh mode.
   * The 2D skin lives in `viewer.floorplanTool.scene` — outside this object's
   * child tree — so hiding the skeleton with `.visible = false` does NOT hide
   * it. Callers that need to hide the "2D version" of the skeleton (e.g. the
   * annotation blacklist, which hides sketches outside the layout persona) use
   * this to reach the group. In 3D mode the walls are the floor's own
   * representation and stay shown, so this returns null.
   */
  get skinning2DGroup(): THREE.Group | null {
    return this.skinning3DEffective ? null : this.skinningGroup;
  }

  /**
   * The skinning group regardless of 2D/3D mode. Like {@link skinning2DGroup}
   * it lives outside this object's child tree, so hiding the skeleton does
   * NOT hide it. Used by the annotation blacklist to hide a floor-filtered
   * skeleton's walls in BOTH modes — a non-selected floor's 3D walls must not
   * leak into the selected floor's view.
   */
  get skinningVisualsGroup(): THREE.Group | null {
    return this.skinningGroup;
  }

  /** Subscribe to selection changes. Returns an unsubscribe function. */
  subscribeSelectionChanged(callback: () => void): () => void {
    this._selectionSubscribers.add(callback);
    return () => {
      this._selectionSubscribers.delete(callback);
    };
  }

  /**
   * Get the hover state for this skeleton (lazily created). Tools call
   * `getHover().setNode(...)` / `setEdge(...)` from their hit-testers so
   * the rest of the app can read one canonical "what's under the cursor"
   * value. The marker-highlight visual on the hovered node is owned
   * inside `WallSketchHover` itself.
   */
  getHover(): WallSketchHover {
    if (!this._hover) {
      this._hover = new WallSketchHover({
        getWallSketch: () => this,
        onHoverChanged: () => {
          for (const cb of this._hoverSubscribers) {
            try {
              cb();
            } catch (err) {
              console.error('WallSketch hover subscriber threw:', err);
            }
          }
        },
      });
    }
    return this._hover;
  }

  /** Subscribe to hover-target changes. Returns an unsubscribe function. */
  subscribeHoverChanged(callback: () => void): () => void {
    this._hoverSubscribers.add(callback);
    return () => {
      this._hoverSubscribers.delete(callback);
    };
  }

  /** Subscribe to node/edge data changes (every `setData`, including live
   * mid-drag edit-session updates). Returns an unsubscribe function. */
  subscribeDataChanged(callback: () => void): () => void {
    this._dataSubscribers.add(callback);
    return () => {
      this._dataSubscribers.delete(callback);
    };
  }

  /**
   * Load and subscribe to reactive properties from the property system.
   * Call this after the skeleton has been added to the scene and has an identifier.
   */
  loadReactiveProperties(args: { color?: THREE.Color; visible?: boolean } = {}): void {
    if (!this.identifier) {
      console.warn('Cannot load reactive properties: skeleton has no identifier');
      return;
    }

    // Unsubscribe from any existing subscriptions
    this._overlayVisibleSubscription?.unsubscribe();
    this._skinning3DSubscription?.unsubscribe();
    this._skinning2DColorSubscription?.unsubscribe();
    this._skinning2DOutlineColorSubscription?.unsubscribe();
    this._skinning3DOpacitySubscription?.unsubscribe();

    try {
      // Load color properties FIRST so they're available when visualization is created
      // (no constructor args for skinning — always read)
      // WallSketch graph overlay (nodes + edges) visibility.
      const overlayVisibleProperty = getWallSketchOverlayVisibleProperty(this.identifier);
      this._overlayVisible = overlayVisibleProperty.value;
      this._overlayVisibleSubscription = overlayVisibleProperty.subscribeToChanges((value) => {
        this._overlayVisible = value;
        this.applyGroupVisibility();
      });

      const skinning2DColorProperty = getWallSketchSkinning2DColorProperty(this.identifier);
      this._skinning2DColor = skinning2DColorProperty.value;
      this._skinning2DColorSubscription = skinning2DColorProperty.subscribeToChanges((value) => {
        this._skinning2DColor = value;
        this.updateSkinningColors();
      });

      const skinning2DOutlineColorProperty = getWallSketchSkinning2DOutlineColorProperty(this.identifier);
      this._skinning2DOutlineColor = skinning2DOutlineColorProperty.value;
      this._skinning2DOutlineColorSubscription = skinning2DOutlineColorProperty.subscribeToChanges((value) => {
        this._skinning2DOutlineColor = value;
        this.updateSkinningColors();
      });

      // 3D skinning follows the FLOOR's 3D toggle, not a per-skeleton one —
      // the single source of truth is the floor's `floor_mesh3d_enabled`,
      // gated by the floor's `floor_wall_source` dropdown: WALL_SKETCH
      // renders the floor's 3D as these skeleton walls, ROOMS as the floor's
      // outline⊖rooms mesh instead. 2D skinning is always on (transparent),
      // so there's no separate enable to load. When the skeleton isn't
      // linked to a floor yet, 3D stays off.
      const floorId = this.groupIdentifier;
      if (floorId) {
        // Source first — the 3D and heatmap/clip flags below all gate on it.
        this._wallSourceSubscription?.unsubscribe();
        const sourceProperty = getFloorWallSourceProperty(floorId);
        this._wallSourceIsWallSketch = sourceProperty.value === 'WALL_SKETCH';
        this._wallSourceSubscription = sourceProperty.subscribeToChanges((value) => {
          this._wallSourceIsWallSketch = value === 'WALL_SKETCH';
          this._skinning3D = this._floorMesh3DEnabledRaw && this._wallSourceIsWallSketch;
          this.updateSkinningVisualization();
          this.applyEffectiveSdfFlags();
        });

        const floorMesh3DEnabledProperty = getFloorMesh3DEnabledProperty(floorId);
        this._floorMesh3DEnabledRaw = floorMesh3DEnabledProperty.value;
        this._skinning3D = this._floorMesh3DEnabledRaw && this._wallSourceIsWallSketch;
        this._skinning3DSubscription = floorMesh3DEnabledProperty.subscribeToChanges((value) => {
          this._floorMesh3DEnabledRaw = value;
          this._skinning3D = value && this._wallSourceIsWallSketch;
          this.updateSkinningVisualization();
        });

        // 3D wall opacity comes from the same floor 3D-mesh settings, so the
        // skeleton walls match the floor mesh look.
        const floorMesh3DOpacityProperty = getFloorMesh3DOpacityProperty(floorId);
        this._skinning3DOpacity = floorMesh3DOpacityProperty.value;
        this._skinning3DOpacitySubscription = floorMesh3DOpacityProperty.subscribeToChanges((value) => {
          this._skinning3DOpacity = value;
          this.applySkinning3DOpacity();
        });
      } else {
        this._skinning3D = false;
      }

      this.updateSkinningVisualization();

      // Heatmap + point cloud clipping follow the FLOOR's consolidated
      // `floor_heatmap_*` settings (same pattern as the 3D toggle above):
      // the floor panel owns one set of controls, and its
      // `floor_wall_source` dropdown decides whether they drive this
      // skeleton pipeline (WALL_SKETCH) or the floor's outline⊖rooms mesh
      // (ROOMS). When the source is ROOMS — or the sketch isn't linked to a
      // floor — this pipeline stays dark and releases its material slot.
      this._sdfEnabledSubscription?.unsubscribe();
      this._sdfGradientSubscription?.unsubscribe();
      this._sdfMaxDistanceSubscription?.unsubscribe();
      this._sdfClipEnabledSubscription?.unsubscribe();
      this._sdfClipMethodSubscription?.unsubscribe();
      this._sdfClipToleranceSubscription?.unsubscribe();

      if (floorId) {
        const gradientProperty = getFloorHeatmapGradientProperty(floorId);
        this._sdfGradient = gradientProperty.value;
        this._sdfGradientSubscription = gradientProperty.subscribeToChanges((value) => {
          this._sdfGradient = value;
          this.updateSDFForPointClouds();
        });

        const maxDistanceProperty = getFloorHeatmapMaxDistanceProperty(floorId);
        this._sdfMaxDistance = maxDistanceProperty.value;
        this._sdfMaxDistanceSubscription = maxDistanceProperty.subscribeToChanges((value) => {
          this._sdfMaxDistance = value;
          this.scheduleSDFRegeneration();
        });

        // The vertical band is NOT a panel property — it derives from the
        // floor's own height range (see getSdfHeightTolerance), updating via
        // setFloorGroupsData when the floor/ceiling planes change.

        const clipEnabledProperty = getFloorHeatmapClipEnabledProperty(floorId);
        this._floorHeatmapClipEnabled = clipEnabledProperty.value;
        this._sdfClipEnabledSubscription = clipEnabledProperty.subscribeToChanges((value) => {
          this._floorHeatmapClipEnabled = value;
          this.applyEffectiveSdfFlags();
        });

        const clipMethodProperty = getFloorHeatmapClipMethodProperty(floorId);
        this._sdfClipMethod = clipMethodProperty.value;
        this._sdfClipMethodSubscription = clipMethodProperty.subscribeToChanges((value) => {
          this._sdfClipMethod = value;
          this.updateSDFForPointClouds();
        });

        const clipToleranceProperty = getFloorHeatmapClipToleranceProperty(floorId);
        this._sdfClipTolerance = clipToleranceProperty.value;
        this._sdfClipToleranceSubscription = clipToleranceProperty.subscribeToChanges((value) => {
          this._sdfClipTolerance = value;
          this.updateSDFForPointClouds();
        });

        const enabledProperty = getFloorHeatmapEnabledProperty(floorId);
        this._floorHeatmapEnabled = enabledProperty.value;
        this._sdfEnabledSubscription = enabledProperty.subscribeToChanges((value) => {
          this._floorHeatmapEnabled = value;
          this.applyEffectiveSdfFlags();
        });

        // Initial effective state — regenerate only when something is active
        // (avoid a spurious clear stomping another sketch's slot on load).
        this._sdfEnabled = this._wallSourceIsWallSketch && this._floorHeatmapEnabled;
        this._sdfClipEnabled = this._wallSourceIsWallSketch && this._floorHeatmapClipEnabled;
        if (this._sdfEnabled || this._sdfClipEnabled) this.scheduleSDFRegeneration();
      } else {
        this._sdfEnabled = false;
        this._sdfClipEnabled = false;
      }

      // --- Annotation color ---
      this._colorSubscription?.unsubscribe();
      const colorProperty = getAnnotationColorProperty(this.identifier);
      if (args.color !== undefined) colorProperty.value = '#' + this.color.getHexString();
      else this.setColor(new THREE.Color(colorProperty.value));
      this._colorSubscription = colorProperty.subscribeToChanges((value) => {
        this.setColor(new THREE.Color(value));
      });

      // --- Annotation visible ---
      this._visibleSubscription?.unsubscribe();
      const visibleProperty = getAnnotationVisibleProperty(this.identifier);
      if (args.visible !== undefined) visibleProperty.value = this.visible;
      else this.visible = visibleProperty.value;
      this.applyGroupVisibility();
      this._visibleSubscription = visibleProperty.subscribeToChanges((value) => {
        this.visible = value;
        this.applyGroupVisibility();
      });

      // --- Geometry properties (nodes, edges, metadata) ---
      // Floor height is NOT a wall-sketch property — it's derived from the floor
      // group this sketch belongs to (see getFloorHeight / floorGroupsData).
      this._nodesSubscription?.unsubscribe();
      this._edgesSubscription?.unsubscribe();
      this._metadataSubscription?.unsubscribe();
      this._wallRegionsSubscription?.unsubscribe();

      const nodesProp = getWallSketchNodesProperty(this.identifier);
      const edgesProp = getWallSketchEdgesProperty(this.identifier);
      const metadataProp = getWallSketchMetadataProperty(this.identifier);
      const wallRegionsProp = getWallSketchWallRegionsProperty(this.identifier);

      // Load geometry from properties if not already loaded via constructor initialData
      if (this.nodes.size === 0) {
        const nodes = nodesProp.value;
        const edges = edgesProp.value;
        if (nodes.length > 0 || edges.length > 0) {
          this.setData(nodes, edges, metadataProp.value as string | null | undefined);
        }
      }

      // Wall regions live alongside nodes/edges but track on their own change
      // path — they update only on user-triggered recompute, not on edit.
      this.wallRegions = wallRegionsProp.value ?? [];
      this.updateWallRegionVisualization();

      const onGeometryChange = () => {
        this.setData(nodesProp.value, edgesProp.value, metadataProp.value as string | null | undefined);
      };

      this._nodesSubscription = nodesProp.subscribeToChanges(onGeometryChange);
      this._edgesSubscription = edgesProp.subscribeToChanges(onGeometryChange);
      this._metadataSubscription = metadataProp.subscribeToChanges(onGeometryChange);
      this._wallRegionsSubscription = wallRegionsProp.subscribeToChanges((value) => {
        this.wallRegions = value ?? [];
        this.updateWallRegionVisualization();
      });
    } catch (error) {
      // Property service might not be initialized yet, that's OK
      // Properties will be synced when the UI component mounts
      console.debug('WallSketch reactive properties not yet available:', error);
    }
  }

  /**
   * Set skeleton data from annotation
   */
  setData(nodes: SkeletonNodeData[], edges: SkeletonEdgeData[], metadata?: string | null): void {
    this.nodes.clear();
    this.edges.clear();

    // Legacy backfill: pre-`name` data stored its display label in `id` (timestamp
    // strings like `edge_1a2b3c_xy4z5w`). New writes carry both an opaque UUID
    // `id` and a `name`. Leave `id` alone so existing reference fields
    // (`startNodeId`/`endNodeId`/`edgeIds`/`roomIds`) keep resolving — only fill
    // in `name` from `id` when it's missing.
    for (const node of nodes) {
      const filled = node.name ? node : { ...node, name: node.id };
      this.nodes.set(filled.id, filled);
    }
    for (const edge of edges) {
      const filled = edge.name ? edge : { ...edge, name: edge.id };
      this.edges.set(filled.id, filled);
    }

    this.metadata = metadata || null;

    // Orphan any OPENING annotations whose linked edge in this skeleton just
    // disappeared. Covers every edge-removal path (deleteEdge, deleteNode,
    // dissolveNode, split/merge) without each one having to remember to call
    // a helper. Imported lazily to avoid a hard dep cycle between the
    // skeleton and the annotation classes.
    this.orphanOpeningsForRemovedEdges();

    this.updateVisualization();
    this.updateBoundingSphere();
    // Skinned wall fills depend on the current node/edge layout, so they need
    // a rebuild whenever the data changes. The check keeps the cost off the
    // common case where skinning is disabled.
    if (this._skinningEnabled) {
      this.updateSkinningVisualization();
    }

    for (const cb of this._dataSubscribers) {
      try {
        cb();
      } catch (err) {
        console.error('WallSketch data subscriber threw:', err);
      }
    }
  }

  /**
   * Snapshot of edge ids from the previous `setData` call, used to detect
   * which edges disappeared since then so we can orphan openings that
   * referenced them. Empty on the initial call (so the first load is a
   * no-op — nothing was "removed", since there was nothing before).
   */
  private previousEdgeIdsSnapshot: Set<string> = new Set();

  /**
   * Clear `opening_skeleton_edge_id` on every OPENING annotation whose linked
   * edge was in this skeleton last time but isn't anymore. Edge ids are
   * globally-unique UUIDs, so if the id also appears in some *other*
   * skeleton we treat that as a migration (rare, but possible in weird
   * refactor flows) and skip the clear.
   */
  private orphanOpeningsForRemovedEdges(): void {
    const removed = new Set<string>();
    for (const id of this.previousEdgeIdsSnapshot) {
      if (!this.edges.has(id)) removed.add(id);
    }
    this.previousEdgeIdsSnapshot = new Set(this.edges.keys());
    if (removed.size === 0) return;

    // Defensive: an edge id we lost might have moved to another skeleton.
    // If it still exists anywhere, don't clear the link.
    const stillElsewhere = new Set<string>();
    for (const s of this.viewer.scene.wallSketches) {
      if (s === this) continue;
      for (const id of removed) {
        if (s.edges.has(id)) stillElsewhere.add(id);
      }
    }

    for (const m of this.viewer.scene.measurements) {
      if (!(m instanceof OpeningAnnotation)) continue;
      try {
        const prop = getOpeningWallSketchEdgeIdProperty(m.identifier);
        const linkedEdgeId = prop.value;
        if (!linkedEdgeId) continue;
        if (!removed.has(linkedEdgeId)) continue;
        if (stillElsewhere.has(linkedEdgeId)) continue;
        prop.value = '';
      } catch {
        // Global property service not initialized yet
      }
    }
  }

  /**
   * Get nodes as array
   */
  getNodes(): SkeletonNodeData[] {
    return Array.from(this.nodes.values());
  }

  /**
   * Get edges as array
   */
  /**
   * WALL edges only — the SAFE DEFAULT. The merged outline subgraph
   * (`role:'OUTLINE'`) is a zero-thickness boundary that must stay out of
   * every skin/wall-band/SDF/snapping computation, so it is deliberately
   * absent here: code that needs the outline (or the complete graph for
   * editing/persistence) must opt in via {@link getOutlineEdges} /
   * {@link getAllEdges}.
   */
  getEdges(): SkeletonEdgeData[] {
    return this.getAllEdges().filter((e) => e.role !== 'OUTLINE');
  }

  /** The COMPLETE edge set (walls + merged outline) — for editing, undo
   *  snapshots, persistence, ring extraction and picking. Never feed this
   *  into wall-band computations. */
  getAllEdges(): SkeletonEdgeData[] {
    return Array.from(this.edges.values());
  }

  /** The merged outline subgraph's edges (`role:'OUTLINE'`). */
  getOutlineEdges(): SkeletonEdgeData[] {
    return this.getAllEdges().filter((e) => e.role === 'OUTLINE');
  }

  /** Whether the merged outline subgraph renders/picks right now — the same
   *  rule the legacy OUTLINE annotation follows via the render blacklist. */
  shouldShowOutlineSubgraph(): boolean {
    return !(this.viewer.floorplanRepresentation?.shouldHideOriginalAnnotations(this.groupIdentifier) ?? false);
  }

  /**
   * Floor (base) height for this wall sketch: the floor-plane Z of the floor
   * group it belongs to. Derived from {@link floorGroupsData} (matched by
   * `groupIdentifier`) rather than stored on the sketch — the floor owns the
   * height range, walls just inherit it. Falls back to 0 before the floor
   * groups have been supplied.
   */
  getFloorHeight(): number {
    if (!this.groupIdentifier) return 0;
    const floor = this.floorGroupsData.find((f) => f.groupIdentifier === this.groupIdentifier);
    return floor?.floorHeight ?? 0;
  }

  /**
   * Ceiling (top) height for this wall sketch: the ceiling-plane Z of its floor
   * group, if one is defined. Null when the floor has no paired ceiling.
   */
  getCeilingHeight(): number | null {
    if (!this.groupIdentifier) return null;
    const floor = this.floorGroupsData.find((f) => f.groupIdentifier === this.groupIdentifier);
    return floor?.ceilingHeight ?? null;
  }

  /**
   * Recompute the effective heatmap/clip flags of this skeleton pipeline
   * (raw floor toggles AND source === wall sketch), then regenerate the SDF
   * (any effect active) or release the material slot (none active).
   */
  private applyEffectiveSdfFlags(): void {
    this._sdfEnabled = this._wallSourceIsWallSketch && this._floorHeatmapEnabled;
    this._sdfClipEnabled = this._wallSourceIsWallSketch && this._floorHeatmapClipEnabled;
    if (this._sdfEnabled || this._sdfClipEnabled) this.scheduleSDFRegeneration();
    else this.clearSDFFromPointClouds();
  }

  /**
   * Vertical band (metres above the floor plane) within which the SDF gradient /
   * isolate effects apply. Derived from the floor group's height range
   * (ceiling − floor) rather than a user-set tolerance — the effects should
   * cover the wall band the floor defines. Falls back to {@link FALLBACK_WALL_HEIGHT}
   * when the floor has no paired ceiling.
   */
  private getSdfHeightTolerance(): number {
    const ceiling = this.getCeilingHeight();
    if (ceiling === null) return FALLBACK_WALL_HEIGHT;
    const range = ceiling - this.getFloorHeight();
    return range > 0.1 ? range : FALLBACK_WALL_HEIGHT;
  }

  /**
   * Provide the project's floor groups (heights) so per-element height
   * constraints can resolve `'default'` (next floor / this floor's ceiling) and
   * `{ targetFloorId }`. Re-runs 3D skinning + SDF when the data changes so wall
   * heights and the SDF band (which derives its floor/ceiling from here) update
   * immediately.
   */
  setFloorGroupsData(floors: FloorHeightInfo[]): void {
    this.floorGroupsData = floors;
    this.updateSkinningVisualization();
    // The SDF floor height + vertical band are derived from the floor group, so
    // re-push them to the point-cloud material when the groups change.
    this.updateSDFForPointClouds();
  }

  /**
   * Get metadata
   */
  getMetadata(): string | null {
    return this.metadata;
  }

  /**
   * Capture a regression-test fixture: the full input this skeleton's 3D
   * extrusion depends on, plus a stable summary (and raw dump) of the current
   * extruded wall geometry. Used by the layout debug menu — see
   * {@link SkeletonFixture}.
   */
  captureFixture(description: string, extra?: Pick<SkeletonFixture['input'], 'roomConnectivity' | 'pipelineConfig'>): SkeletonFixture {
    const { summary, raw } = summarizeWallGeometry(this.wallMeshes);
    return {
      description,
      expected: summary,
      input: {
        nodes: this.getNodes(),
        edges: this.getAllEdges(),
        floorHeight: this.getFloorHeight(),
        metadata: this.metadata,
        wallRegions: this.getWallRegions(),
        floorGroups: this.floorGroupsData,
        roomConnectivity: extra?.roomConnectivity,
        pipelineConfig: extra?.pipelineConfig,
      },
      rawGeometry: raw,
    };
  }

  /**
   * The identifier of the outline annotation this skeleton was built
   * from. Stored inside the freeform `metadata` JSON so we don't have
   * to extend the shared annotation schema. Persisted so a manual
   * regenerate uses the same wall-region envelope as the initial
   * generation — without this the regenerate would re-synthesize an
   * envelope from the rooms and drift away from the user's curated
   * outline.
   */
  getOutlineIdentifier(): string | null {
    if (!this.metadata) return null;
    try {
      const parsed = JSON.parse(this.metadata) as { outlineIdentifier?: string };
      return parsed.outlineIdentifier ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Set (or clear) the linked outline identifier in the metadata JSON.
   * Other metadata fields are preserved. Returns the new metadata
   * string so callers can forward it through the `edit_skeleton`
   * persistence event.
   */
  setOutlineIdentifier(outlineIdentifier: string | null): string {
    let parsed: Record<string, unknown> = {};
    if (this.metadata) {
      try {
        const p = JSON.parse(this.metadata);
        if (p && typeof p === 'object') parsed = p as Record<string, unknown>;
      } catch {
        // Leave parsed empty — corrupt metadata gets rewritten.
      }
    }
    if (outlineIdentifier === null) {
      delete parsed.outlineIdentifier;
    } else {
      parsed.outlineIdentifier = outlineIdentifier;
    }
    this.metadata = JSON.stringify(parsed);
    return this.metadata;
  }

  /**
   * Legacy OUTLINE annotations that were converted into this sketch's merged
   * `role:'OUTLINE'` subgraph. The annotations themselves are kept as hidden
   * tombstones (recovery source, undo safety); this record says which ones
   * this sketch superseded. Stored in the freeform metadata JSON like
   * `outlineIdentifier`.
   */
  getConvertedOutlineIds(): string[] {
    if (!this.metadata) return [];
    try {
      const parsed = JSON.parse(this.metadata) as { convertedOutlineIds?: string[] };
      return Array.isArray(parsed.convertedOutlineIds) ? parsed.convertedOutlineIds : [];
    } catch {
      return [];
    }
  }

  /** Record converted legacy outline ids (merged with any already stored).
   *  Returns the new metadata string for `edit_skeleton` forwarding. */
  setConvertedOutlineIds(outlineIds: string[]): string {
    let parsed: Record<string, unknown> = {};
    if (this.metadata) {
      try {
        const p = JSON.parse(this.metadata);
        if (p && typeof p === 'object') parsed = p as Record<string, unknown>;
      } catch {
        // Leave parsed empty — corrupt metadata gets rewritten.
      }
    }
    const existing = Array.isArray(parsed.convertedOutlineIds) ? (parsed.convertedOutlineIds as string[]) : [];
    parsed.convertedOutlineIds = [...new Set([...existing, ...outlineIds])];
    this.metadata = JSON.stringify(parsed);
    return this.metadata;
  }

  /**
   * Get node by ID
   */
  getNode(id: string): SkeletonNodeData | undefined {
    return this.nodes.get(id);
  }

  /**
   * Get edge by ID
   */
  getEdge(id: string): SkeletonEdgeData | undefined {
    return this.edges.get(id);
  }

  /**
   * Get node marker for raycasting
   */
  getNodeMarker(nodeId: string): SphereMarker | undefined {
    return this.nodeMarkerMap.get(nodeId);
  }

  /**
   * Get node mesh by ID (returns the mesh inside the SphereMarker)
   * Used for raycasting to determine which node was clicked
   */
  getNodeMesh(nodeId: string): THREE.Mesh | undefined {
    const marker = this.nodeMarkerMap.get(nodeId);
    return marker?.mesh;
  }

  /**
   * Get all node markers for raycasting
   */
  getAllNodeMarkers(): SphereMarker[] {
    return this.spheres;
  }

  /**
   * Get all node meshes for raycasting (returns the mesh inside each SphereMarker)
   */
  getAllNodeMeshes(): THREE.Mesh[] {
    return this.spheres.map((marker) => marker.mesh);
  }

  /**
   * Clear visualization
   */
  private clearVisualization(): void {
    // Dispose and remove node markers
    for (const marker of this.spheres) {
      marker.dispose();
      this.nodeGroup.remove(marker);
    }
    this.spheres = [];
    this.nodeMarkerMap.clear();

    // Note: We don't dispose/recreate line geometry here
    // We'll update it in place in updateVisualization like PointoramaRoom does
  }

  /**
   * Update visualization from data
   * Uses relative vertex positions to avoid floating-point precision issues with large world coordinates.
   * The Object3D is positioned at a reference point, and all vertices are relative to that point.
   * This follows the same pattern as Measure.ts for line rendering.
   */
  private updateVisualization(): void {
    this.clearVisualization();

    // The merged outline subgraph follows the exact rule the legacy OUTLINE
    // annotation obeys (shouldHideOriginalAnnotations): hidden in the Design
    // tab and in Wall+SKINNED, visible in Wall+Measured and everywhere else.
    // `updateSkinningVisualization` re-runs this build when the state flips.
    const includeOutline = this.shouldShowOutlineSubgraph();
    this._outlineInCenterline = includeOutline;

    const zOffset = this.getFloorHeight() + 0.02;

    // Find reference point (first node position) for relative coordinates
    // This avoids floating-point precision issues with large world coordinates
    let referencePoint: THREE.Vector3 | null = null;
    const nodesArray = Array.from(this.nodes.values());
    if (nodesArray.length > 0) {
      const firstNode = nodesArray[0];
      referencePoint = new THREE.Vector3(firstNode.position.x, firstNode.position.y, zOffset);
    }

    // Position this Object3D at the reference point (or origin if no nodes)
    if (referencePoint) {
      this.position.set(referencePoint.x, referencePoint.y, referencePoint.z);
    } else {
      this.position.set(0, 0, 0);
    }

    // Build line segments for all edges using RELATIVE positions
    // LineSegmentsGeometry expects positions as flat array [x1,y1,z1, x2,y2,z2, ...]
    // Each pair of consecutive vertices forms a line segment
    const vertices: number[] = [];

    for (const edge of this.edges.values()) {
      if (edge.role === 'OUTLINE' && !includeOutline) continue;
      const startNode = this.nodes.get(edge.startNodeId);
      const endNode = this.nodes.get(edge.endNodeId);

      if (!startNode || !endNode) continue;

      // Curved edges tessellate to a polyline; straight edges stay [start, end]
      const polyline = tessellateEdge(
        { x: startNode.position.x, y: startNode.position.y },
        { x: endNode.position.x, y: endNode.position.y },
        edge.bulge,
      );
      for (let i = 0; i + 1 < polyline.length; i++) {
        const a = polyline[i];
        const b = polyline[i + 1];
        if (referencePoint) {
          // Use RELATIVE positions (subtract reference point) to avoid precision issues
          // Z is already at zOffset via Object3D position
          vertices.push(a.x - referencePoint.x, a.y - referencePoint.y, 0);
          vertices.push(b.x - referencePoint.x, b.y - referencePoint.y, 0);
        } else {
          // Fallback to absolute if no reference (shouldn't happen with edges)
          vertices.push(a.x, a.y, zOffset);
          vertices.push(b.x, b.y, zOffset);
        }
      }
    }

    // Update geometry positions (similar to PointoramaRoom pattern)
    // Dispose and recreate geometry to force update
    const geometry = this.lineGeometry;
    geometry.dispose();

    if (vertices.length > 0) {
      this.lineGeometry = new LineSegmentsGeometry();
      this.lineGeometry.setPositions(vertices);

      // Update references on Line2 objects
      this.line.geometry = this.lineGeometry;
      this.backgroundLine.geometry = this.lineGeometry;

      this.line.computeLineDistances();
      this.backgroundLine.computeLineDistances();
    } else {
      // No edges - create empty geometry
      this.lineGeometry = new LineSegmentsGeometry();
      this.line.geometry = this.lineGeometry;
      this.backgroundLine.geometry = this.lineGeometry;
    }

    // Mark materials as needing update
    this.lineMaterial.needsUpdate = true;
    this.backgroundLineMaterial.needsUpdate = true;

    // Create node visualizations using SphereMarker (same as PointoramaRoom)
    // Use RELATIVE positions for node markers as well
    for (const node of this.nodes.values()) {
      // Outline-only nodes hide with their edges outside the Measured view.
      if (!includeOutline && node.edgeIds.length > 0 && node.edgeIds.every((id) => this.edges.get(id)?.role === 'OUTLINE')) continue;
      const marker = new SphereMarker(this.viewer);
      marker.setColor(this.color);
      // 1.2 in the layout editor (larger than room markers — easier to grab), shrunken elsewhere.
      marker.setScaleFactor(this.viewer.scene.wallSketchSphereScale);
      marker.setRenderOrder(NODE_MARKER_RENDER_ORDER); // above the wall meshes (1–5)

      if (referencePoint) {
        // Use RELATIVE position
        marker.position.set(
          node.position.x - referencePoint.x,
          node.position.y - referencePoint.y,
          0, // Z is already at zOffset via Object3D position
        );
      } else {
        marker.position.set(node.position.x, node.position.y, zOffset);
      }
      marker.name = `node_${node.id}`;

      this.spheres.push(marker);
      this.nodeMarkerMap.set(node.id, marker);
      this.nodeGroup.add(marker);
    }
  }

  /**
   * Update bounding sphere
   */
  private updateBoundingSphere(): void {
    if (this.nodes.size === 0) {
      this.boundingSphere = undefined;
      return;
    }

    const points: THREE.Vector3[] = [];
    for (const node of this.nodes.values()) {
      points.push(new THREE.Vector3(node.position.x, node.position.y, node.position.z));
    }

    const boundingBox = new THREE.Box3().setFromPoints(points);
    this.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
  }

  /**
   * Get the color
   */
  getColor(): THREE.Color {
    return this.color;
  }

  /**
   * Set the color
   */
  setColor(color: THREE.Color): void {
    this.color = color;
    // Update all node markers
    for (const marker of this.spheres) {
      marker.setColor(color);
    }
    // Update line materials
    this.lineMaterial.color = color;
    this.backgroundLineMaterial.color = color;
    if (this.wallRegionMaterial) this.wallRegionMaterial.color = color;
  }

  /**
   * Get the persisted wall regions (read-only). Use {@link recomputeWallRegions}
   * to update them from the current rooms.
   */
  getWallRegions(): WallRegionData[] {
    return this.wallRegions;
  }

  /**
   * Replace the wall regions and persist via the reactive property system.
   * Caller is responsible for ensuring the input is correct (e.g. produced by
   * {@link buildWallRegionFromRooms}). Triggers an automatic visual rebuild
   * via the property subscription.
   */
  setWallRegions(regions: WallRegionData[]): void {
    if (!this.identifier) {
      // No identifier yet — just stash + render. The constructor's
      // initialData path doesn't go through properties.
      this.wallRegions = regions;
      this.updateWallRegionVisualization();
      return;
    }
    // Writing the property fires our own subscribeToChanges → which calls
    // updateWallRegionVisualization, so we don't need to re-render here.
    getWallSketchWallRegionsProperty(this.identifier).value = regions;
  }

  /**
   * Recompute wall regions from the given rooms, using `wallThickness` as the
   * outward inflation distance, and persist via the property system. Intended
   * to be called from a user-triggered action (no UX yet — call directly from
   * the editor or a future toolbar button).
   *
   * The Vector2-based component shape used internally is converted to plain
   * `{x, y}` pairs so the result round-trips through JSON cleanly.
   */
  recomputeWallRegions(rooms: Array<{ identifier: string; polygon: THREE.Vector3[] }>, wallThickness: number): void {
    const components = buildWallRegionFromRooms(rooms, wallThickness);
    const persisted: WallRegionData[] = components.map((c) => ({
      outerRing: c.outerRing.map((p) => ({ x: p.x, y: p.y })),
      holes: c.holes.map((h) => h.map((p) => ({ x: p.x, y: p.y }))),
    }));
    this.setWallRegions(persisted);
  }

  /**
   * Tear down the wall-region outline overlay, if any.
   *
   * The skeleton no longer draws its own outline of `this.wallRegions`: that
   * silhouette is owned and rendered by the linked {@link PointoramaOutline}
   * object (see {@link getOutlineIdentifier}). This used to build a
   * LineSegments2 overlay from the regions, which double-drew the outline in the
   * skeleton's color. We keep the `wallRegions` *data* (persisted, used for
   * regeneration + fixtures) but never visualize it here. The method stays so
   * the data setters can call it as a cheap teardown.
   */
  private updateWallRegionVisualization(): void {
    // Tear down any existing visual (e.g. built before this change).
    if (this.wallRegionLine && this.wallRegionGroup) {
      this.wallRegionGroup.remove(this.wallRegionLine);
    }
    if (this.wallRegionGeometry) {
      this.wallRegionGeometry.dispose();
      this.wallRegionGeometry = null;
    }
    if (this.wallRegionMaterial) {
      this.wallRegionMaterial.dispose();
      this.wallRegionMaterial = null;
    }
    this.wallRegionLine = null;
  }

  /**
   * Select the skeleton (highlight)
   */
  select(): void {
    this.selected = true;
    // Show background line
    this.backgroundLineMaterial.opacity = 0.25;
    // Highlight all node markers
    for (const marker of this.spheres) {
      marker.highlight();
      marker.showBorder();
    }
  }

  /**
   * Deselect the skeleton
   */
  deselect(): void {
    this.selected = false;
    // Hide background line
    this.backgroundLineMaterial.opacity = 0.0;
    // Reset all node markers
    for (const marker of this.spheres) {
      marker.unHighlight();
      marker.hideBorder();
    }
  }

  /**
   * Update method called by viewer's render loop
   * Updates line material resolution to match current canvas size (same as PointoramaRoom)
   */
  update(): void {
    const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
    this.lineMaterial.resolution.set(renderAreaSize.width, renderAreaSize.height);
    this.backgroundLineMaterial.resolution.set(renderAreaSize.width, renderAreaSize.height);
  }

  /**
   * Clone the skeleton
   */
  clone(): this {
    const cloned = new PointoramaWallSketch(this.viewer, this.color);
    cloned.identifier = this.identifier;
    cloned.groupIdentifier = this.groupIdentifier;
    cloned.setData(this.getNodes(), this.getAllEdges(), this.metadata);
    return cloned as this;
  }

  // ============================================================================
  // SKINNING VISUALIZATION METHODS
  // ============================================================================

  /**
   * Update only the colors of existing skinning materials without regenerating geometry
   */
  private updateSkinningColors(): void {
    if (!this._skinningEnabled) return;

    // 2D mode bakes the fill color into vertex colors (so selected edges can
    // override their slice). Material color must stay white. Repaint the
    // vertex-color buffer in-place to honour the new wall color and any
    // current selection without rebuilding geometry.
    if (!this.skinning3DEffective) {
      this.repaintSkinningVertexColors();
    } else {
      // 3D mode uses a single flat material color — cheap to update.
      for (const mesh of this.wallMeshes) {
        const material = mesh.material as THREE.MeshBasicMaterial;
        if (material && material.color) material.color.set(this._skinning2DColor);
      }
    }

    // Outline + glyph lines render through the backdrop-invert shader (see
    // buildWallOutline) — the configured outline color no longer applies.
  }

  /**
   * Update the 3D wall opacity in-place (driven by the floor's
   * `floor_mesh3d_opacity`). Only the 3D extruded walls carry this opacity;
   * 2D skinning keeps its own fixed transparent fill.
   */
  private applySkinning3DOpacity(): void {
    if (!this.skinning3DEffective) return;
    const opacity = THREE.MathUtils.clamp(this._skinning3DOpacity, 0, 1);
    for (const mesh of this.wallMeshes) {
      const material = mesh.material as THREE.MeshBasicMaterial;
      if (!material) continue;
      material.opacity = opacity;
      material.transparent = opacity < 1;
      material.depthWrite = opacity >= 1;
      material.needsUpdate = true;
    }
  }

  /**
   * Effective Measured-representation state for THIS sketch's floor: the
   * floor's `floor_wall_source` is ROOMS (the panel's "measured") AND the
   * floorplan persona's Wall/Design tab is active. In that state the skin
   * renders outline-only with the skeleton error overlay (see
   * buildOriginalRepresentation).
   */
  private isOriginalRepresentation(): boolean {
    return this.viewer.floorplanRepresentation?.isOriginalActive(this.groupIdentifier) ?? false;
  }

  /**
   * Sync visibility of the groups that live in other scenes (not descendants
   * of `this`, so they don't inherit `this.visible`). The 3D walls are the
   * floor's representation, so they stay shown in 3D mode regardless; the
   * wall-region outline and 2D fill follow `this.visible`. In the ORIGINAL
   * representation the skinning group holds the flat outline⊖rooms fill, so
   * the 3D always-visible exception doesn't apply.
   */
  private applyGroupVisibility(): void {
    if (this.wallRegionGroup) this.wallRegionGroup.visible = this.visible;
    if (this.skinningGroup) this.skinningGroup.visible = this.skinning3DEffective && !this.isOriginalRepresentation() ? true : this.visible;

    // The skeleton graph overlay (node markers + edge lines) follows both the
    // skeleton's own visibility and the `skeleton_overlay_visible` toggle, so it
    // can be hidden for a clean floor plan while the wall skin stays shown.
    // The Design tab is the clean RESULT view — the graph always hides there.
    // The Measured (ORIGINAL) representation hides the graph too — ALWAYS,
    // selection included (selecting/box-selecting the outline must not pop
    // the stale interior skeleton into view; the selection highlight is its
    // own overlay mesh and the outline itself renders via the skin
    // boundary). Broken-outline red segments override via _hasOutlineError
    // below — they're the only visible+fixable depiction of the outline then.
    const measuredIdle = this.isOriginalRepresentation();
    const overlayBase = this.visible && this._overlayVisible && !(this.viewer.floorplanRepresentation?.isDesignActive() ?? false);
    const overlay = overlayBase && !measuredIdle;
    // Measured-idle keeps the OUTLINE's node markers visible: the outline
    // stays interactive there (hover/select/move), and without its grab
    // handles that is undiscoverable. Only the outline-attached markers
    // show; the hidden skeleton's markers follow the graph hide.
    const outlineNodesOnly = overlayBase && measuredIdle;
    this.nodeGroup.visible = overlay || outlineNodesOnly;
    if (this.nodeGroup.visible) {
      for (const [nodeId, marker] of this.nodeMarkerMap) {
        const node = this.nodes.get(nodeId);
        const attachedToOutline = !!node && node.edgeIds.some((id) => this.edges.get(id)?.role === 'OUTLINE');
        marker.visible = overlay || attachedToOutline;
      }
    }
    this.line.visible = overlay;
    this.backgroundLine.visible = overlay;
    // The error overlay is part of the skeleton graph, Measured view only.
    // A broken (unclosed) outline overrides the measured-idle hide: its red
    // segments are the only visible+fixable depiction of the outline then
    // (closed rings render via the skin boundary instead).
    this.errorLine.visible = this.isOriginalRepresentation() && this.visible && (overlay || this._hasOutlineError);
  }

  /** Whether the outline has open (unclosed) chains — set by
   *  updateSkeletonErrorOverlay, read by applyGroupVisibility to keep the
   *  red error segments visible through the measured-idle graph hide. */
  private _hasOutlineError = false;

  /** Repaint the 2D wall mesh's vertex colors based on current selection + base color. */
  private repaintSkinningVertexColors(): void {
    // Vertex-range info lives only in updateSkinningVisualization, so the
    // simplest correct implementation is to rebuild. In practice this runs
    // only on color/selection changes and is already used elsewhere.
    this.updateSkinningVisualization();
  }

  /**
   * Update skinning visualization based on current state.
   *
   * Per-node "hollow polygon" approach: at every node, intersect the
   * offset wall lines of all connected edges (via `computeNodeCorners`)
   * to get the corner points. Each edge then has 4 corners (one per
   * end-side). The wall surface is the union of per-edge quads built
   * from those corners — no junction fan needed, because adjacent edges
   * already share the same intersection corners, so quads meet exactly.
   *
   * Selection highlighting is intentionally not driven by per-triangle
   * edge attribution here — that's done via a separate overlay.
   */
  updateSkinningVisualization(): void {
    this.clearSkinningVisualization();

    // Keep the Measured error overlay in step with every representation /
    // geometry rebuild (it empties itself in the Wall-sketch representation).
    this.updateSkeletonErrorOverlay();

    if (!this._skinningEnabled || !this.skinningGroup) return;

    const floorplanScene = this.viewer.floorplanTool?.scene;
    if (floorplanScene && !floorplanScene.children.includes(this.skinningGroup)) {
      floorplanScene.add(this.skinningGroup);
    }
    // Mode may have changed / group just re-added — resync visibility.
    this.applyGroupVisibility();

    // Representation flips change whether the merged outline subgraph is part
    // of the centerline build (Measured shows it, Wall-sketch hides it) —
    // rebuild the graph overlay when the state changed.
    if (this._outlineInCenterline !== this.shouldShowOutlineSubgraph() && this.getOutlineEdges().length > 0) {
      this.updateVisualization();
    }

    const nodes = this.getNodes();
    const edges = this.getEdges(); // wall edges only — outline is never skinned/banded
    if (nodes.length === 0 || edges.length === 0) return;

    // Localize the skinning group: position it at the first node and build all
    // skinning geometry RELATIVE to that point. Like the line geometry / node
    // markers, this keeps vertices near the origin and avoids float32 jitter at
    // UTM-scale absolute coords. Each builder subtracts the ref while the
    // coordinates are still float64; `localizeSkinningGroup` (called at the end
    // of each build path) only places the group.
    const refNode = nodes[0];
    this._skinningRef.set(refNode.position.x, refNode.position.y, 0);

    // ORIGINAL representation (floorplan persona wall switch): the wall fill is
    // the zone BETWEEN the floor's room polygons and its outline (outline ⊖
    // rooms) with the door/window cutouts punched out — tied to the room and
    // outline annotation lines instead of the wall-sketch edge band.
    if (this.isOriginalRepresentation()) {
      this.buildOriginalRepresentation(this.getFloorHeight());
      return;
    }

    const nodeMap = new Map<string, SkeletonNodeData>();
    for (const node of nodes) nodeMap.set(node.id, node);

    const edgeOffsets = new Map<string, EdgeOffsetData>();
    for (const edge of edges) {
      const startNode = nodeMap.get(edge.startNodeId);
      const endNode = nodeMap.get(edge.endNodeId);
      if (!startNode || !endNode) continue;
      edgeOffsets.set(edge.id, this.computeEdgeOffsets(edge, startNode, endNode));
    }

    // The wall partition (computeWallRegions) is each edge's offset RECTANGLE
    // with the half-plane KERNEL cells of its two end nodes carved out. 3D mode
    // extrudes + unions those regions per-element-height; 2D mode (below) flattens
    // the SAME partition to one footprint via Manifold's robust CrossSection
    // boolean and triangulates it — so both views share one clean solve.
    const z = this.getFloorHeight();

    // Map each node to its incident edge ids (used by the kernel partition).
    const nodeEdgeIds = new Map<string, string[]>();
    for (const edge of edges) {
      const od = edgeOffsets.get(edge.id);
      if (!od) continue;
      const a = nodeEdgeIds.get(od.startNode.id) ?? [];
      a.push(edge.id);
      nodeEdgeIds.set(od.startNode.id, a);
      const b = nodeEdgeIds.get(od.endNode.id) ?? [];
      b.push(edge.id);
      nodeEdgeIds.set(od.endNode.id, b);
    }

    // 3D mode: extrude each partition region to its own [bottom, top] and union
    // them into a watertight solid (door/window openings cut from that). 2D mode
    // below renders the SAME partition's flat footprint. Effective mode: 2D
    // (orthographic) main views always render the flat skin, regardless of
    // the floor's mesh3D property.
    if (this.skinning3DEffective) {
      this.createExtrudedWallsPerElement(edges, nodeMap, edgeOffsets, nodeEdgeIds, z);
      this.localizeSkinningGroup();
      return;
    }

    // Flat 2D skinning from the SAME partition the 3D extrusion uses. The clean
    // footprint (wall rects MINUS node cells, unioned) is computed ONCE in 2D via
    // Manifold's robust CrossSection boolean, then triangulated + outlined here —
    // so the 2D skin matches the 3D extrusion exactly. Until the WASM module is
    // ready we fall back to a simple per-region fill and rebuild once it loads.
    const regions = this.computeWallRegions(edges, nodeMap, edgeOffsets, nodeEdgeIds, this.getFloorHeight());
    if (regions.length === 0) return;

    const allVertices: number[] = [];
    const outlinePositions: number[] = [];

    const opening2D = isManifoldReady() ? this.buildOpening2DData(z) : { cutterRects: [], glyphSegments: [], selectedGlyphSegments: [] };
    const footprint = isManifoldReady() ? this.computeFootprint2D(regions, opening2D.cutterRects) : null;
    if (footprint) {
      this.appendFootprint2DGeometry(footprint, z, allVertices, outlinePositions);
    } else {
      // WASM not ready: simple per-region fill (walls overlap cells — invisible
      // at 40% opacity), and rebuild once Manifold loads.
      for (const region of regions) {
        if (region.ring.length < 3) continue;
        const flat: number[] = [];
        for (const p of region.ring) flat.push(p.x, p.y);
        const tris = earcut(flat, null, 2);
        for (let i = 0; i < tris.length; i += 3) {
          const a = tris[i];
          const b = tris[i + 1];
          const c = tris[i + 2];
          allVertices.push(flat[a * 2], flat[a * 2 + 1], z, flat[b * 2], flat[b * 2 + 1], z, flat[c * 2], flat[c * 2 + 1], z);
        }
      }
      this._manifoldReadyUnsub?.();
      this._manifoldReadyUnsub = onManifoldReady(() => {
        this._manifoldReadyUnsub = null;
        this.updateSkinningVisualization();
      });
    }

    if (allVertices.length > 0) {
      this.buildFlatWallMesh(allVertices, []);
    }
    if (outlinePositions.length > 0) {
      this.buildWallOutline(outlinePositions);
    }
    if (opening2D.glyphSegments.length > 0) {
      this.buildOpeningGlyphs(opening2D.glyphSegments, false);
    }
    if (opening2D.selectedGlyphSegments.length > 0) {
      this.buildOpeningGlyphs(opening2D.selectedGlyphSegments, true);
    }
    this.localizeSkinningGroup();
  }

  /**
   * Triangulate a Manifold 2D footprint (outer rings + holes) at height `z`,
   * appending fill triangles to `fillVertices` and boundary segments to
   * `outlinePositions` (both flat [x,y,z,...] arrays).
   */
  private appendFootprint2DGeometry(footprint: FootprintPolygon[], z: number, fillVertices: number[], outlinePositions: number[]): void {
    for (const poly of footprint) {
      const flat: number[] = [];
      const holeIdx: number[] = [];
      for (const p of poly.outer) flat.push(p[0], p[1]);
      for (const hole of poly.holes) {
        holeIdx.push(flat.length / 2);
        for (const p of hole) flat.push(p[0], p[1]);
      }
      const tris = earcut(flat, holeIdx.length > 0 ? holeIdx : null, 2);
      for (let i = 0; i < tris.length; i += 3) {
        const a = tris[i];
        const b = tris[i + 1];
        const c = tris[i + 2];
        fillVertices.push(flat[a * 2], flat[a * 2 + 1], z, flat[b * 2], flat[b * 2 + 1], z, flat[c * 2], flat[c * 2 + 1], z);
      }
      for (const ring of [poly.outer, ...poly.holes]) {
        for (let i = 0; i < ring.length; i++) {
          const a = ring[i];
          const b = ring[(i + 1) % ring.length];
          outlinePositions.push(a[0], a[1], z, b[0], b[1], z);
        }
      }
    }
  }

  /**
   * Build the Measured-representation visuals: the measured wall footprint
   * (floor outline ⊖ cavities ⊖ rooms, door/window cutouts punched out via
   * the same CSG as before) rendered as its boundary OUTLINE only — no fill —
   * with the opening glyphs drawn in the gaps. On top of that, the skeleton
   * error overlay tints centerline segments that violate the measured
   * geometry (outside the floor outline / inside a room) red. The fill will
   * return as an export / per-sketch option later.
   */
  private buildOriginalRepresentation(z: number): void {
    if (!isManifoldReady()) {
      // Same deferred-rebuild fallback as the skinned 2D path — the outline ⊖
      // rooms boolean needs the CrossSection WASM, so show nothing until then.
      this._manifoldReadyUnsub?.();
      this._manifoldReadyUnsub = onManifoldReady(() => {
        this._manifoldReadyUnsub = null;
        this.updateSkinningVisualization();
      });
      return;
    }

    const opening2D = this.buildOpening2DData(z);
    const footprint = this.computeOriginalFootprint2D(opening2D.cutterRects);
    if (footprint) {
      const fillVertices: number[] = [];
      const outlinePositions: number[] = [];
      this.appendFootprint2DGeometry(footprint, z, fillVertices, outlinePositions);
      if (outlinePositions.length > 0) this.buildWallOutline(outlinePositions);
    }
    if (opening2D.glyphSegments.length > 0) this.buildOpeningGlyphs(opening2D.glyphSegments, false);
    if (opening2D.selectedGlyphSegments.length > 0) this.buildOpeningGlyphs(opening2D.selectedGlyphSegments, true);
    this.localizeSkinningGroup();
  }

  /**
   * The floor's outline as ring components: this sketch's own merged
   * `role:'OUTLINE'` cycles first, the legacy OUTLINE annotation as fallback
   * for unconverted projects. Null when the floor has neither.
   */
  private resolveMeasuredOutlineRings(): { outerRing: { x: number; y: number }[]; holes: { x: number; y: number }[][] }[] | null {
    const merged = extractOutlineRings(this.getNodes(), this.getAllEdges());
    if (merged.length > 0) return merged;

    const floorId = this.groupIdentifier;
    if (!floorId) return null;
    const outline = this.viewer.scene.outlines.find((o) => o.groupIdentifier === floorId);
    if (!outline?.identifier) return null;
    try {
      const outerRing = getOutlineOuterRingProperty(outline.identifier).value.map((c) => ({ x: c.x, y: c.y }));
      if (outerRing.length < 3) return null;
      const holes = getOutlineCavitiesProperty(outline.identifier).value.map((ring) => ring.map((c) => ({ x: c.x, y: c.y })));
      return [{ outerRing, holes }];
    } catch {
      return null;
    }
  }

  /**
   * The as-built outline rings, with the rooms as the last resort: a floor
   * converted from its rooms without an outline (drawn or merged) still gets
   * the band around them, so the Measured representation, its opening cutouts
   * and the export do not come up empty.
   */
  private resolveMeasuredOutlineRingsOrDerived(): { outerRing: { x: number; y: number }[]; holes: { x: number; y: number }[][] }[] | null {
    const rings = this.resolveMeasuredOutlineRings();
    if (rings && rings.length > 0) return rings;
    const floorId = this.groupIdentifier;
    if (!floorId) return null;
    const derived = deriveOutlineRingsFromRooms(this.viewer.scene.rooms.filter((room) => room.groupIdentifier === floorId && !!room.identifier));
    return derived.length > 0 ? derived : null;
  }

  /**
   * The Measured representation's wall footprint: the floor's outline rings
   * minus their cavity rings and room polygons, minus the opening cutouts.
   * Pure read — the legacy outline/room geometry SUBSCRIPTIONS are owned by
   * updateSkeletonErrorOverlay (same properties, one owner); merged-outline
   * changes rebuild via setData. Returns null when the floor has no usable
   * outline.
   */
  private computeOriginalFootprint2D(cutters: { x: number; y: number }[][]): FootprintPolygon[] | null {
    const floorId = this.groupIdentifier;
    if (!floorId) return null;
    const rings = this.resolveMeasuredOutlineRingsOrDerived();
    if (!rings || rings.length === 0) return null;

    const roomPolygons: { x: number; y: number }[][] = [];
    for (const room of this.viewer.scene.rooms) {
      if (room.groupIdentifier !== floorId || !room.identifier) continue;
      try {
        const poly = getRoomPolygonProperty(room.identifier).value.map((c) => ({ x: c.x, y: c.y }));
        if (poly.length >= 3) roomPolygons.push(poly);
      } catch {
        // Room polygon not in the registry yet — skip it this build.
      }
    }

    const regions = rings.map((component) => ({
      ring: component.outerRing,
      bottom: 0,
      top: 0,
      subtract: [...component.holes.filter((ring) => ring.length >= 3), ...roomPolygons],
    }));
    return regionsToFootprint2D(regions, cutters);
  }

  /**
   * The measured geometry the error overlay classifies against: the floor's
   * outline rings (merged subgraph first, legacy annotation fallback) and its
   * room polygons. Also (re)subscribes to the LEGACY geometry so property
   * edits rebuild the overlay — the subscriptions live in `_originalGeomSubs`
   * (cleared on each rebuild); merged-outline edits rebuild via setData.
   * `outerRings` empty = no outside test this build.
   */
  private collectMeasuredErrorGeometry(): { outerRings: { x: number; y: number }[][]; cavities: { x: number; y: number }[][]; rooms: { x: number; y: number }[][] } {
    const result: { outerRings: { x: number; y: number }[][]; cavities: { x: number; y: number }[][]; rooms: { x: number; y: number }[][] } = { outerRings: [], cavities: [], rooms: [] };
    const floorId = this.groupIdentifier;
    if (!floorId) return result;

    const merged = extractOutlineRings(this.getNodes(), this.getAllEdges());
    if (merged.length > 0) {
      for (const component of merged) {
        result.outerRings.push(component.outerRing);
        result.cavities.push(...component.holes.filter((ring) => ring.length >= 3));
      }
    } else {
      const outline = this.viewer.scene.outlines.find((o) => o.groupIdentifier === floorId);
      if (outline?.identifier) {
        try {
          const outerRingProperty = getOutlineOuterRingProperty(outline.identifier);
          const cavitiesProperty = getOutlineCavitiesProperty(outline.identifier);
          const ring = outerRingProperty.value.map((c) => ({ x: c.x, y: c.y }));
          if (ring.length >= 3) result.outerRings.push(ring);
          result.cavities = cavitiesProperty.value.map((cavity) => cavity.map((c) => ({ x: c.x, y: c.y }))).filter((cavity) => cavity.length >= 3);
          this._originalGeomSubs.push(outerRingProperty.subscribeToChanges(() => this.scheduleOriginalRebuild()));
          this._originalGeomSubs.push(cavitiesProperty.subscribeToChanges(() => this.scheduleOriginalRebuild()));
        } catch {
          // outline properties not in the registry yet — no outside test this build
        }
      }
    }

    for (const room of this.viewer.scene.rooms) {
      if (room.groupIdentifier !== floorId || !room.identifier) continue;
      try {
        const polygonProperty = getRoomPolygonProperty(room.identifier);
        const poly = polygonProperty.value.map((c) => ({ x: c.x, y: c.y }));
        if (poly.length >= 3) result.rooms.push(poly);
        this._originalGeomSubs.push(polygonProperty.subscribeToChanges(() => this.scheduleOriginalRebuild()));
      } catch {
        // Room polygon not in the registry yet — skip it this build.
      }
    }
    return result;
  }

  /** Ray-cast point-in-ring on XY for {x,y} rings. */
  private static pointInRingXY(x: number, y: number, ring: { x: number; y: number }[]): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i].x;
      const yi = ring[i].y;
      const xj = ring[j].x;
      const yj = ring[j].y;
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * Rebuild the Measured view's red skeleton error overlay: every WALL
   * centerline segment whose midpoint lies OUTSIDE the floor's outline rings
   * (or inside one of their cavities) or INSIDE a room polygon is duplicated
   * into the red error line — the sketch should run within walls, i.e. inside
   * the outline and between the rooms. Merged `role:'OUTLINE'` edges are
   * exempt from that classification (they ARE the boundary — their midpoints
   * sit exactly on it); instead, outline edges on OPEN chains (ring not
   * closed) go red as the "unclosed outline" signal. Empty (and hidden) in
   * the Wall-sketch representation. Positions are RELATIVE to this Object3D's
   * position, like the centerline.
   */
  private updateSkeletonErrorOverlay(): void {
    for (const sub of this._originalGeomSubs) sub.unsubscribe();
    this._originalGeomSubs = [];

    const vertices: number[] = [];
    this._hasOutlineError = false;
    if (this.isOriginalRepresentation()) {
      const geometry = this.collectMeasuredErrorGeometry();
      const ref = this.position;
      const openOutlineEdgeIds = collectOpenOutlineEdgeIds(this.getNodes(), this.getAllEdges());
      // A broken (unclosed) outline can't render as the measured skin
      // boundary — its red error segments are then the ONLY depiction, so
      // applyGroupVisibility must show them even while the measured-idle
      // rule hides the rest of the graph.
      this._hasOutlineError = openOutlineEdgeIds.size > 0;
      for (const edge of this.edges.values()) {
        const startNode = this.nodes.get(edge.startNodeId);
        const endNode = this.nodes.get(edge.endNodeId);
        if (!startNode || !endNode) continue;
        const polyline = tessellateEdge({ x: startNode.position.x, y: startNode.position.y }, { x: endNode.position.x, y: endNode.position.y }, edge.bulge);
        if (edge.role === 'OUTLINE') {
          // Unclosed outline chains render fully red; closed rings are clean.
          if (openOutlineEdgeIds.has(edge.id)) {
            for (let i = 0; i + 1 < polyline.length; i++) {
              vertices.push(polyline[i].x - ref.x, polyline[i].y - ref.y, 0, polyline[i + 1].x - ref.x, polyline[i + 1].y - ref.y, 0);
            }
          }
          continue;
        }
        for (let i = 0; i + 1 < polyline.length; i++) {
          const a = polyline[i];
          const b = polyline[i + 1];
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          const outside = geometry.outerRings.length > 0 && !geometry.outerRings.some((ring) => PointoramaWallSketch.pointInRingXY(mx, my, ring));
          const inCavity = !outside && geometry.cavities.some((cavity) => PointoramaWallSketch.pointInRingXY(mx, my, cavity));
          const inRoom = !outside && !inCavity && geometry.rooms.some((room) => PointoramaWallSketch.pointInRingXY(mx, my, room));
          if (outside || inCavity || inRoom) {
            vertices.push(a.x - ref.x, a.y - ref.y, 0, b.x - ref.x, b.y - ref.y, 0);
          }
        }
      }
    }

    this.errorLineGeometry.dispose();
    this.errorLineGeometry = new LineSegmentsGeometry();
    if (vertices.length > 0) this.errorLineGeometry.setPositions(vertices);
    this.errorLine.geometry = this.errorLineGeometry;
    if (vertices.length > 0) this.errorLine.computeLineDistances();
    this.errorLineMaterial.needsUpdate = true;
    this.applyGroupVisibility();
  }

  /**
   * The current wall-partition rings (world XY) — the same partition the wall
   * skin fills. Exposed for interior-area computations (a room's SKINNED area
   * is its polygon minus these rings). Recomputed on call; empty when the
   * sketch has no geometry.
   */
  getWallRegionRings(): { x: number; y: number }[][] {
    const nodes = this.getNodes();
    const edges = this.getEdges(); // wall edges only — outline is never skinned/banded
    if (nodes.length === 0 || edges.length === 0) return [];

    const nodeMap = new Map<string, SkeletonNodeData>();
    for (const node of nodes) nodeMap.set(node.id, node);

    const edgeOffsets = new Map<string, EdgeOffsetData>();
    for (const edge of edges) {
      const startNode = nodeMap.get(edge.startNodeId);
      const endNode = nodeMap.get(edge.endNodeId);
      if (!startNode || !endNode) continue;
      edgeOffsets.set(edge.id, this.computeEdgeOffsets(edge, startNode, endNode));
    }

    const nodeEdgeIds = new Map<string, string[]>();
    for (const edge of edges) {
      const od = edgeOffsets.get(edge.id);
      if (!od) continue;
      const a = nodeEdgeIds.get(od.startNode.id) ?? [];
      a.push(edge.id);
      nodeEdgeIds.set(od.startNode.id, a);
      const b = nodeEdgeIds.get(od.endNode.id) ?? [];
      b.push(edge.id);
      nodeEdgeIds.set(od.endNode.id, b);
    }

    const regions = this.computeWallRegions(edges, nodeMap, edgeOffsets, nodeEdgeIds, this.getFloorHeight());
    return regions.map((r) => r.ring.map((p) => ({ x: p.x, y: p.y })));
  }

  /**
   * Coalesced rebuild for outline/room geometry edits while the ORIGINAL
   * representation is shown. Deferred to a microtask so a property change
   * dispatch never re-enters updateSkinningVisualization (which unsubscribes
   * and resubscribes these listeners) mid-notification.
   */
  private scheduleOriginalRebuild(): void {
    if (this._originalRebuildQueued) return;
    this._originalRebuildQueued = true;
    queueMicrotask(() => {
      this._originalRebuildQueued = false;
      if (this.isOriginalRepresentation()) this.updateSkinningVisualization();
    });
  }

  /**
   * Rebuild ONLY the door/window glyph overlay lines, leaving the wall mesh,
   * footprint outline and node cells untouched. Used when a glyph's visual
   * changes (door type, swing, selection highlight) — the opening's cutout hole
   * doesn't move, so the expensive wall geometry / Manifold footprint don't need
   * recomputing. Falls back to a full rebuild only when there's no skin yet.
   */
  rebuildOpeningGlyphsOnly(): void {
    if (!this._skinningEnabled || !this.skinningGroup || (this.skinning3DEffective && !this.isOriginalRepresentation())) return;
    // No skin built yet (or WASM not ready) → nothing to patch; a full pass will
    // run via the normal path. In the ORIGINAL representation the fill is flat
    // (never the 3D extrusion), so the glyph-only fast path works there too.
    if (this.wallMeshes.length === 0 || !isManifoldReady()) {
      this.updateSkinningVisualization();
      return;
    }

    this.disposeGlyphLines();

    const z = this.getFloorHeight();
    const opening2D = this.buildOpening2DData(z);
    if (opening2D.glyphSegments.length > 0) this.buildOpeningGlyphs(opening2D.glyphSegments, false);
    if (opening2D.selectedGlyphSegments.length > 0) this.buildOpeningGlyphs(opening2D.selectedGlyphSegments, true);
    // The glyphs are built relative to the same `_skinningRef` the rest of the
    // skin was, and the group is already placed there.
  }

  /** Dispose + detach the two opening-glyph lines (normal + selected). */
  private disposeGlyphLines(): void {
    for (const key of ['glyph', 'selectedGlyph'] as const) {
      const line = key === 'glyph' ? this.glyphLine : this.selectedGlyphLine;
      const geom = key === 'glyph' ? this.glyphGeometry : this.selectedGlyphGeometry;
      const mat = key === 'glyph' ? this.glyphMaterial : this.selectedGlyphMaterial;
      if (line) this.skinningGroup?.remove(line);
      geom?.dispose();
      mat?.dispose();
      if (key === 'glyph') {
        this.glyphLine = null;
        this.glyphGeometry = null;
        this.glyphMaterial = null;
      } else {
        this.selectedGlyphLine = null;
        this.selectedGlyphGeometry = null;
        this.selectedGlyphMaterial = null;
      }
    }
  }

  /**
   * Place the skinning group at `_skinningRef`. Its child geometry is already
   * built relative to the ref (every builder subtracts it while the coords are
   * still float64), so the group position puts it back in world space.
   */
  private localizeSkinningGroup(): void {
    if (!this.skinningGroup) return;
    this.skinningGroup.position.set(this._skinningRef.x, this._skinningRef.y, 0);
  }

  /**
   * A flat `[x,y,z, ...]` world-space position array relative to
   * `_skinningRef`, still in float64 — call it before the numbers go into a
   * float32 buffer, never after.
   */
  private toLocalFlat(positions: number[]): number[] {
    const ref = this._skinningRef;
    const local = new Array<number>(positions.length);
    for (let i = 0; i < positions.length; i += 3) {
      local[i] = positions[i] - ref.x;
      local[i + 1] = positions[i + 1] - ref.y;
      local[i + 2] = positions[i + 2];
    }
    return local;
  }

  /**
   * The 2D footprint of the wall partition (wall rects with node cells carved
   * out, unioned) as grouped outer+hole contours, via Manifold's robust
   * CrossSection boolean. Returns null when the WASM module isn't ready.
   */
  private computeFootprint2D(regions: WallRegion[], cutters?: { x: number; y: number }[][]): FootprintPolygon[] | null {
    const inputs = regions.map((r) => ({
      ring: r.ring.map((p) => ({ x: p.x, y: p.y })),
      bottom: r.bottom,
      top: r.top,
      subtract: r.subtract?.map((cell) => cell.map((p) => ({ x: p.x, y: p.y }))),
    }));
    return regionsToFootprint2D(inputs, cutters);
  }

  /**
   * Build the flat 2D wall mesh from a triangle-soup of positions. Vertex
   * colors are uniform fill color (selection highlighting is handled by
   * a separate overlay).
   */
  private buildFlatWallMesh(allVertices: number[], _triangleEdgeIds: string[]): void {
    if (!this.skinningGroup) return;
    const vertexCount = allVertices.length / 3;
    const baseColor = new THREE.Color(this._skinning2DColor);
    const colors = new Float32Array(vertexCount * 3);
    for (let v = 0; v < vertexCount; v++) {
      colors[v * 3] = baseColor.r;
      colors[v * 3 + 1] = baseColor.g;
      colors[v * 3 + 2] = baseColor.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.toLocalFlat(allVertices)), 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      transparent: true,
      opacity: PointoramaWallSketch.SKINNING_2D_OPACITY,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.renderOrder = 1;
    this.skinningGroup.add(mesh);
    this.wallMeshes.push(mesh);
  }

  private buildWallOutline(outlinePositions: number[]): void {
    if (!this.skinningGroup) return;
    const rendererSize = new THREE.Vector2();
    this.viewer.renderer.getSize(rendererSize);

    this.outlineGeometry = new LineSegmentsGeometry();
    this.outlineGeometry.setPositions(this.toLocalFlat(outlinePositions));

    this.outlineMaterial = new LineMaterial({
      color: '#ffffff',
      linewidth: this.OUTLINE_WIDTH,
      resolution: rendererSize,
      depthTest: true,
    });
    // Per-pixel inversion of the captured backdrop, tinted in the brand
    // accent — bright tint over dark, dark tint over light, sampled beneath
    // each fragment.
    applyBackdropInvert(this.outlineMaterial, this.viewer.backdropCapture);
    this.outlineLine = new LineSegments2(this.outlineGeometry, this.outlineMaterial);
    this.outlineLine.renderOrder = 2;
    this.outlineLine.computeLineDistances();
    this.skinningGroup.add(this.outlineLine);
  }

  /**
   * Build the door/window glyph overlay (floor-plan symbols) from flat line
   * segment positions `[x,y,z, x,y,z, ...]`. Rendered above the wall outline
   * with the same outline color/width, through the same LineSegments2 path,
   * relative to `_skinningRef` like the rest of the skin.
   */
  private buildOpeningGlyphs(glyphPositions: number[], selected: boolean): void {
    if (!this.skinningGroup) return;
    const rendererSize = new THREE.Vector2();
    this.viewer.renderer.getSize(rendererSize);

    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(this.toLocalFlat(glyphPositions));

    const material = new LineMaterial({
      color: selected ? SKELETON_SELECTION_COLOR.getHex() : '#ffffff',
      linewidth: this.OUTLINE_WIDTH,
      resolution: rendererSize,
      depthTest: true,
    });
    // Non-selected glyphs invert the backdrop like the skin outline;
    // selected ones keep the solid selection color.
    if (!selected) applyBackdropInvert(material, this.viewer.backdropCapture);

    const line = new LineSegments2(geometry, material);
    // Selected glyphs draw just above the normal glyphs so the highlight wins.
    line.renderOrder = selected ? 4 : 3;
    line.computeLineDistances();
    this.skinningGroup.add(line);

    if (selected) {
      this.selectedGlyphGeometry = geometry;
      this.selectedGlyphMaterial = material;
      this.selectedGlyphLine = line;
    } else {
      this.glyphGeometry = geometry;
      this.glyphMaterial = material;
      this.glyphLine = line;
    }
  }

  /**
   * Clear skinning visualization
   */
  private clearSkinningVisualization(): void {
    // Drop ORIGINAL-representation geometry listeners; the next original-mode
    // build resubscribes to the then-current outline/rooms.
    for (const sub of this._originalGeomSubs) sub.unsubscribe();
    this._originalGeomSubs = [];

    for (const mesh of this.wallMeshes) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) mesh.material.dispose();
      this.skinningGroup?.remove(mesh);
    }
    this.wallMeshes = [];
    // Drop stale glyph hit-test geometry; rebuilt on the next skinning pass.
    this._openingGlyphSegments.clear();

    if (this.outlineLine) {
      this.skinningGroup?.remove(this.outlineLine);
      this.outlineLine = null;
    }
    if (this.outlineGeometry) {
      this.outlineGeometry.dispose();
      this.outlineGeometry = null;
    }
    if (this.outlineMaterial) {
      this.outlineMaterial.dispose();
      this.outlineMaterial = null;
    }

    this.disposeGlyphLines();

    // Remove skinning group from floorplanTool.scene if skinning is disabled
    if (!this._skinningEnabled && this.skinningGroup) {
      const floorplanScene = this.viewer.floorplanTool?.scene;
      if (floorplanScene && floorplanScene.children.includes(this.skinningGroup)) {
        floorplanScene.remove(this.skinningGroup);
      }
    }
  }

  /**
   * Resolve the absolute bottom/top world-Z bounds for one skeleton edge/node
   * height constraint, against the project's floor groups and this skeleton's
   * floor. Offsets always apply, even with no floor data; a `default` top with
   * no floor data falls back to a fixed wall height above the floor.
   */
  private resolveElementBounds(constraint: SkeletonNodeData['height'] | SkeletonEdgeData['height'], floorHeight: number): { bottom: number; top: number } {
    return resolveSkeletonConstraint(constraint, this.floorGroupsData, floorHeight);
  }

  /**
   * Resolve an edge's absolute bottom/top world-Z bounds (its wall's base and
   * head), using the same per-element height resolution as the 3D extrusion.
   * Unknown edge → the skeleton's default constraint. Used by IFC export so the
   * exported wall height matches the rendered 3D wall.
   */
  getEdgeExtrusionZ(edgeId: string): { bottom: number; top: number } {
    return this.resolveElementBounds(this.edges.get(edgeId)?.height, this.getFloorHeight());
  }

  /**
   * Compute the shared geometry inputs for skinning: node map, per-edge offset
   * lines, node→edge adjacency, and the mitered corner points. Mirrors the
   * inline computation in updateSkinningVisualization; reused by
   * getPartitionSegments so the debug overlay matches the rendered partition.
   */
  private buildSkeletonGeometryInputs(): {
    edges: SkeletonEdgeData[];
    nodeMap: Map<string, SkeletonNodeData>;
    edgeOffsets: Map<string, EdgeOffsetData>;
    nodeEdgeIds: Map<string, string[]>;
    cornerPoints: Map<string, THREE.Vector2>;
  } | null {
    const nodes = this.getNodes();
    const edges = this.getEdges(); // wall edges only — outline is never skinned/banded
    if (nodes.length === 0 || edges.length === 0) return null;

    const nodeMap = new Map<string, SkeletonNodeData>();
    for (const node of nodes) nodeMap.set(node.id, node);

    const edgeOffsets = new Map<string, EdgeOffsetData>();
    for (const edge of edges) {
      const startNode = nodeMap.get(edge.startNodeId);
      const endNode = nodeMap.get(edge.endNodeId);
      if (!startNode || !endNode) continue;
      edgeOffsets.set(edge.id, this.computeEdgeOffsets(edge, startNode, endNode));
    }

    const nodeEdgeIds = new Map<string, string[]>();
    for (const edge of edges) {
      const od = edgeOffsets.get(edge.id);
      if (!od) continue;
      const a = nodeEdgeIds.get(od.startNode.id) ?? [];
      a.push(edge.id);
      nodeEdgeIds.set(od.startNode.id, a);
      const b = nodeEdgeIds.get(od.endNode.id) ?? [];
      b.push(edge.id);
      nodeEdgeIds.set(od.endNode.id, b);
    }

    const cornerPoints = new Map<string, THREE.Vector2>();
    for (const [nodeId, eIds] of nodeEdgeIds) {
      const node = nodeMap.get(nodeId);
      if (!node) continue;
      this.computeNodeCorners(nodeId, node, eIds, edgeOffsets, cornerPoints);
    }

    return { edges, nodeMap, edgeOffsets, nodeEdgeIds, cornerPoints };
  }

  /**
   * Partition the wall footprint into per-element regions using the SDF /
   * bisector model — see `computeSdfWallRegions`. Each wall edge becomes
   * a capsule SDF, and the partition is the Voronoi-style decomposition by
   * argmin of capsule SDFs. Bisectors at junctions never overshoot, so
   * adjacent walls don't crook each other (which the prior
   * offset-line-intersection model could do for short connectors between
   * thick walls).
   *
   * No separate node-cell regions are emitted — each junction's kernel is
   * absorbed by the adjacent walls along their shared bisector. Per-EDGE
   * heights are honoured; per-NODE heights inherit from the adjacent
   * edges at the junction.
   *
   * `edgeOffsets` and `nodeEdgeIds` are kept in the signature for the
   * call sites that still pass them, but are unused — the SDF partition
   * derives its inputs directly from `edges` + `nodeMap`.
   */
  private computeWallRegions(edges: SkeletonEdgeData[], nodeMap: Map<string, SkeletonNodeData>, _edgeOffsets: Map<string, EdgeOffsetData>, _nodeEdgeIds: Map<string, string[]>, floorHeight: number): WallRegion[] {
    return computeSdfWallRegions(edges, nodeMap, floorHeight, (constraint, fh) => this.resolveElementBounds(constraint, fh));
  }

  /**
   * 2D boundary segments for the debug "Show Wall Split Lines" overlay, at the
   * skeleton floor Z: the wall FOOTPRINT silhouette PLUS each node junction
   * KERNEL CELL ring — so every junction cell is visible as its own isolated
   * region (walls capping against it), even though the rendered mesh is one
   * unioned solid. Every segment lies on a real wall offset line. Returns [] when
   * geometry can't be built.
   */
  getPartitionSegments(): Array<{ a: THREE.Vector3; b: THREE.Vector3 }> {
    const built = this.buildSkeletonGeometryInputs();
    if (!built) return [];
    const regions = this.computeWallRegions(built.edges, built.nodeMap, built.edgeOffsets, built.nodeEdgeIds, this.getFloorHeight());
    const z = this.getFloorHeight();
    const out: Array<{ a: THREE.Vector3; b: THREE.Vector3 }> = [];
    const pushRing = (ring: Array<{ x: number; y: number } | [number, number]>) => {
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const ax = Array.isArray(a) ? a[0] : a.x;
        const ay = Array.isArray(a) ? a[1] : a.y;
        const bx = Array.isArray(b) ? b[0] : b.x;
        const by = Array.isArray(b) ? b[1] : b.y;
        out.push({ a: new THREE.Vector3(ax, ay, z), b: new THREE.Vector3(bx, by, z) });
      }
    };
    // Wall silhouette (footprint contours).
    const footprint = this.computeFootprint2D(regions);
    if (footprint) {
      for (const poly of footprint) {
        pushRing(poly.outer);
        for (const hole of poly.holes) pushRing(hole);
      }
    }
    // Each node kernel cell as its own ring → cells shown isolated.
    for (const region of regions) {
      if (region.isNodeCell) pushRing(region.ring);
    }
    return out;
  }

  /**
   * Candidate face circles for arc re-fitting exported outlines: each curved
   * edge contributes its two offset (face) circles, concentric with the
   * centerline circle. Sizes and clamps mirror the SDF partition's
   * tessellation (`buildCapsule` / `tessellateBandPair`) exactly, so
   * candidates coincide with the circles that were tessellated.
   */
  private buildArcCandidates(edges: SkeletonEdgeData[], nodeMap: Map<string, SkeletonNodeData>): ArcCandidate[] {
    const out: ArcCandidate[] = [];
    for (const edge of edges) {
      if (!edgeIsCurved(edge)) continue;
      const a = nodeMap.get(edge.startNodeId);
      const b = nodeMap.get(edge.endNodeId);
      if (!a || !b) continue;
      const arc = arcFromBulge({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge as number);
      if (!arc) continue;
      const s = Math.sign(edge.bulge as number);
      out.push({ center: arc.center, radius: arc.radius + (edge.leftSize || 0.1) * s });
      out.push({ center: arc.center, radius: Math.max(1e-4, arc.radius - (edge.rightSize || 0.1) * s) });
    }
    return out;
  }

  /**
   * 2D data for the layout DXF export: the wall footprint outline loops with
   * door/window openings cut out (exactly what the 2D skin renders), plus the
   * per-opening glyph inputs (the export rebuilds each glyph in a local frame
   * to emit shared BLOCK definitions). Null when the sketch has no geometry.
   * Falls back to the un-cut outline while the Manifold WASM module isn't
   * ready.
   *
   * Curved walls: tessellated arc runs in the loops are collapsed back to
   * true arcs — `bulges[i]` (INTERNAL sign convention, see arcGeometry) curves
   * the segment points[i] → points[(i+1) % length]; 0 = straight.
   */
  getLayoutExport2D(): { outlineLoops: Array<{ points: Array<{ x: number; y: number; z: number }>; bulges: number[] }>; openings: OpeningGlyphInput[] } | null {
    const built = this.buildSkeletonGeometryInputs();
    const z = this.getFloorHeight();

    // The Measured (as-built) representation exports what its 2D skin shows:
    // the band between the outline and the rooms with the openings cut out,
    // not the authored wall-sketch band — and it needs no wall edges at all.
    if (this.isOriginalRepresentation()) {
      // Selected openings are included — selection is UI state, not geometry.
      const opening2D = this.buildOpening2DData(z);
      const toLoop = (ring: Array<{ x: number; y: number }>) => ({ points: ring.map((p) => ({ x: p.x, y: p.y, z })), bulges: new Array<number>(ring.length).fill(0) });
      const footprint = isManifoldReady() ? this.computeOriginalFootprint2D(opening2D.cutterRects) : null;
      if (footprint) {
        const outlineLoops: Array<{ points: Array<{ x: number; y: number; z: number }>; bulges: number[] }> = [];
        for (const poly of footprint) {
          for (const ring of [poly.outer, ...poly.holes]) {
            if (ring.length >= 3) outlineLoops.push(toLoop(ring.map(([x, y]) => ({ x, y }))));
          }
        }
        return { outlineLoops, openings: opening2D.openings };
      }
      // Manifold not ready: the outline rings and the rooms as they are, uncut.
      const rings = this.resolveMeasuredOutlineRingsOrDerived() ?? [];
      if (rings.length === 0 && opening2D.openings.length === 0) return null;
      const outlineLoops = rings.flatMap((component) => [component.outerRing, ...component.holes].filter((ring) => ring.length >= 3).map(toLoop));
      return { outlineLoops, openings: opening2D.openings };
    }

    if (!built) return null;
    // Selected openings are included — selection is UI state, not geometry.
    const opening2D = this.buildOpening2DData(z);
    const openings = opening2D.openings;

    const candidates = this.buildArcCandidates(built.edges, built.nodeMap);
    const refitLoop = (ring: Array<{ x: number; y: number }>): { points: Array<{ x: number; y: number; z: number }>; bulges: number[] } => {
      if (candidates.length === 0) {
        return { points: ring.map((p) => ({ x: p.x, y: p.y, z })), bulges: new Array<number>(ring.length).fill(0) };
      }
      const refit = refitArcsToRing(ring, candidates);
      return { points: refit.points.map((p) => ({ x: p.x, y: p.y, z })), bulges: refit.bulges };
    };

    const regions = this.computeWallRegions(built.edges, built.nodeMap, built.edgeOffsets, built.nodeEdgeIds, z);
    const footprint = regions.length > 0 && isManifoldReady() ? this.computeFootprint2D(regions, opening2D.cutterRects) : null;
    if (!footprint) {
      return { outlineLoops: this.getWallOutlineLoops().map(refitLoop), openings };
    }

    const outlineLoops: Array<{ points: Array<{ x: number; y: number; z: number }>; bulges: number[] }> = [];
    for (const poly of footprint) {
      for (const ring of [poly.outer, ...poly.holes]) {
        if (ring.length >= 3) outlineLoops.push(refitLoop(ring.map(([x, y]) => ({ x, y }))));
      }
    }
    return { outlineLoops, openings };
  }

  /**
   * Build the extruded 3D wall mesh with **per-element heights**.
   *
   * Each wall edge becomes a slab (its 4 mitered corner points) extruded from
   * `floorHeight` to the edge's resolved top height. Each node becomes its
   * junction polygon (the mitered corner points around it, in angular order)
   * extruded to the node's resolved top height. Because walls and node
   * polygons share the same corner points, they tile without gaps.
   *
   * Vertical faces are emitted per undirected boundary segment: exterior
   * segments (touched by one region) get a full-height face; segments shared
   * by two regions of different heights get a "step" face spanning the two
   * tops. Shared segments of equal height get no face (interior, hidden).
   */
  private createExtrudedWallsPerElement(edges: SkeletonEdgeData[], nodeMap: Map<string, SkeletonNodeData>, edgeOffsets: Map<string, EdgeOffsetData>, nodeEdgeIds: Map<string, string[]>, floorHeight: number): void {
    if (!this.skinningGroup) return;

    // Relative to `_skinningRef` before anything is built: the stitched mesh and
    // Manifold both write float32 vertices, and the crease outline is derived
    // from those.
    const ref = this._skinningRef;
    const toLocal = (p: THREE.Vector2) => new THREE.Vector2(p.x - ref.x, p.y - ref.y);
    const regions = this.computeWallRegions(edges, nodeMap, edgeOffsets, nodeEdgeIds, floorHeight).map((r) => ({ ...r, ring: r.ring.map(toLocal), subtract: r.subtract?.map((cell) => cell.map(toLocal)) }));
    if (regions.length === 0) return;

    // Uncut base solid: stitch the per-element regions for display + as the
    // fallback when CSG isn't available yet. (The stitched base may be non-
    // manifold at shallow junctions, but it renders fine with DoubleSide.)
    const acc = buildStitchedWallMesh(regions);
    if (acc.indices.length === 0) return;

    let geometry: THREE.BufferGeometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(acc.positions, 3));
    geometry.setIndex(acc.indices);

    // ALWAYS route through Manifold union — even with zero cutters. The
    // stitched base mesh assumes the per-region polygons TILE (share exact
    // boundary vertices), which the SDF partition can't strictly guarantee
    // at short-connector junctions where caps collapse to the node.
    // Manifold's per-region extrude-and-union is robust to those overlaps
    // and produces a clean 2-manifold every time. Falls back to the stitched
    // base while WASM is still loading.
    const cutters = this.buildOpeningCutters(floorHeight, { origin: ref });
    if (isManifoldReady()) {
      const regionInputs = regions.map((r) => ({
        ring: r.ring.map((p) => ({ x: p.x, y: p.y })),
        bottom: r.bottom,
        top: r.top,
        subtract: r.subtract?.map((cell) => cell.map((p) => ({ x: p.x, y: p.y }))),
      }));
      const cut = subtractFromRegions(regionInputs, cutters);
      if (cut) {
        geometry.dispose();
        geometry = cut.geometry;
      }
    } else {
      // WASM not loaded yet — render the stitched base for now, rebuild
      // once it's ready.
      this._manifoldReadyUnsub?.();
      this._manifoldReadyUnsub = onManifoldReady(() => {
        this._manifoldReadyUnsub = null;
        this.updateSkinningVisualization();
      });
    }
    cutters.forEach((c) => c.geometry.dispose());

    if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();

    const opacity = THREE.MathUtils.clamp(this._skinning3DOpacity, 0, 1);
    const material = new THREE.MeshBasicMaterial({
      color: this._skinning2DColor,
      side: THREE.DoubleSide,
      transparent: opacity < 1,
      opacity,
      depthTest: true,
      depthWrite: opacity >= 1,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.renderOrder = 1;
    this.skinningGroup.add(mesh);
    this.wallMeshes.push(mesh);

    // Outline overlay: derive crease edges from the FINAL geometry (the cut
    // result), exactly like the floor 3D mesh. This makes the outline follow the
    // wall silhouette AND the door/window reveals (the holes), and hides the
    // coplanar internal triangles CSG emits. 10° crease threshold matches floors.
    // Debug: when `showAllTriangles` is on, skip the crease filter and emit
    // every triangle edge so the raw triangulation is visible for inspection.
    const outlineEdges = PointoramaWallSketch._showAllTriangles ? extractAllTriangleEdges(geometry) : computeCreaseEdges(geometry, 10);
    if (outlineEdges.length > 0) {
      const rendererSize = new THREE.Vector2();
      this.viewer.renderer.getSize(rendererSize);

      this.outlineGeometry = new LineSegmentsGeometry();
      this.outlineGeometry.setPositions(outlineEdges);
      this.outlineMaterial = new LineMaterial({
        color: this._skinning2DOutlineColor,
        linewidth: this.OUTLINE_WIDTH,
        resolution: rendererSize,
        depthTest: true,
      });
      this.outlineLine = new LineSegments2(this.outlineGeometry, this.outlineMaterial);
      this.outlineLine.renderOrder = 2;
      this.outlineLine.computeLineDistances();
      this.skinningGroup.add(this.outlineLine);
    }
  }

  /**
   * Build Manifold cutter geometries for every door/window opening that belongs
   * to one of this skeleton's rooms (via `opening_room_ids` ∩ the rooms
   * referenced by this skeleton's edges/nodes). Openings are NOT linked to
   * skeleton edges, so we match by room and let the opening's own 3D polygon
   * (which already lies on the wall plane) define the cutter shape — its PCA
   * gives the wall axis/normal. Each cutter is a prism punched through the wall,
   * the same shape the floor 3D mesh subtracts.
   */
  buildOpeningCutters(floorHeight: number, options: { measuredFaces?: boolean; tag?: (identifier: string) => string; origin?: { x: number; y: number } } = {}): TaggedCutter[] {
    const tagOf = options.tag ?? ((identifier: string) => identifier);
    // The cutters are float32 geometry: built relative to `origin` (the caller's
    // local frame) so they stay precise far from the world origin.
    const originX = options.origin?.x ?? 0;
    const originY = options.origin?.y ?? 0;
    // Rooms that belong to this skeleton (referenced by its edges + nodes).
    const skeletonRoomIds = new Set<string>();
    for (const e of this.getEdges()) {
      for (const rid of e.roomIds) skeletonRoomIds.add(rid);
    }
    for (const n of this.getNodes()) for (const rid of n.roomIds) skeletonRoomIds.add(rid);
    if (skeletonRoomIds.size === 0) return [];

    // Vertical extent for the cutter cap-extension: the skeleton's default wall
    // band (this floor → next floor / ceiling). Openings sit within this.
    const bounds = resolveSkeletonConstraint(undefined, this.floorGroupsData, floorHeight);

    const cutters: TaggedCutter[] = [];
    // Wall holes are driven by OPENING_SKETCHes on this wall sketch's edges
    // (parametric: offset/length along the edge, lower/upper bound vertically).
    // The legacy floorplan OPENINGs no longer cut the wall — they're replicated
    // into opening sketches on floor→sketch conversion.
    for (const os of this.viewer.scene.openingSketches) {
      // Only THIS floor's openings — STRICT match. The edge-id lookup below is
      // NOT enough of a filter: generated edge ids repeat across floors
      // (rs_e_1, ...), so another floor's opening would resolve against this
      // floor's same-named edge and cut a phantom hole here. A group-less
      // opening (dragged to root / stale data) must not cut on ANY floor.
      if (os.groupIdentifier !== this.groupIdentifier) continue;
      let edgeId = '';
      let offset: number;
      let length: number;
      let lower: number;
      let upper: number;
      try {
        edgeId = getOpeningSketchWallEdgeIdProperty(os.identifier).value || '';
        offset = getOpeningSketchOffsetProperty(os.identifier).value ?? 0;
        length = getOpeningSketchLengthProperty(os.identifier).value ?? 0;
        lower = getOpeningSketchLowerBoundProperty(os.identifier).value ?? bounds.bottom;
        // `height` is stored relative to the ground offset; head Z = lower + height.
        // Fall back to the wall band's top when no height is set.
        const heightVal = getOpeningSketchHeightProperty(os.identifier).value;
        upper = heightVal != null ? lower + heightVal : bounds.top;
      } catch {
        continue; // property service not ready
      }
      if (!edgeId || length <= 1e-4) continue;
      const edge = this.getEdge(edgeId);
      if (!edge) continue; // edge belongs to another wall sketch
      const a = this.nodes.get(edge.startNodeId);
      const b = this.nodes.get(edge.endNodeId);
      if (!a || !b) continue;

      // The cutter footprint is THIS edge's own band over the opening span,
      // with a small clearance past each face — never a generous sweep. The
      // old "overshoot is harmless" box (maxWallSize·2 + 0.4) crossed the
      // junction whenever an opening sat near a corner and carved a chunk out
      // of the ADJACENT wall; with the opening clamped to the corner-clear
      // span, the edge's own band + margin stays inside its mitered quad
      // except for the few-cm clearance. Matches the 2D pass's cut shape.
      const CUT_FACE_CLEARANCE = 0.04;
      let leftSize = edge.leftSize ?? 0.1;
      let rightSize = edge.rightSize ?? 0.1;
      // The as-built mesh (outline ⊖ rooms) is not the authored band: its faces
      // are where the rooms and the outline are. Measure them at the opening
      // (straight edges only; capped like the 2D cutout, see buildOpening2DData)
      // and fall back per side to the authored size where no face is found.
      if (options.measuredFaces && !edgeIsCurved(edge)) {
        const MAX_CUTOUT_FACE_SIZE_M = 0.6;
        const measuredFrame = frameFromWallOpeningSketch({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, { offset, length, perpOffset: 0 });
        if (measuredFrame && measuredFrame.halfWidth > 1e-4) {
          const faces = this.measuredWallFaceSizesAt(measuredFrame);
          if (faces.left !== null && faces.left <= MAX_CUTOUT_FACE_SIZE_M) leftSize = faces.left;
          if (faces.right !== null && faces.right <= MAX_CUTOUT_FACE_SIZE_M) rightSize = faces.right;
        }
      }
      const cutLeft = leftSize + CUT_FACE_CLEARANCE;
      const cutRight = rightSize + CUT_FACE_CLEARANCE;

      // Curved wall: a straight box would miss the wall mass by the span's
      // sagitta (≈3 cm for a 1 m door on a 1.5 m-radius wall). Cut with a
      // vertical prism over the opening span's arc-band footprint instead.
      if (edgeIsCurved(edge)) {
        const span = subSpan({ x: a.position.x - originX, y: a.position.y - originY }, { x: b.position.x - originX, y: b.position.y - originY }, edge.bulge, offset, offset + length);
        // Extend past the lower/upper bounds the same way buildOpeningCutter's
        // cap extension does, so CSG pierces floor/ceiling caps cleanly.
        const ring = tessellateBand(span.start, span.end, span.bulge, cutLeft, cutRight);
        const cutter = buildVerticalPrismCutter(ring, lower - OPENING_INFLATE, upper + OPENING_INFLATE);
        if (cutter) cutters.push({ geometry: cutter, tag: tagOf(os.identifier) });
        continue;
      }

      // The wall HOLE stays on the centerline (perpOffset = 0); perpOffset only
      // shifts the drawn glyph, never the cutout.
      const frame = frameFromWallOpeningSketch({ x: a.position.x - originX, y: a.position.y - originY }, { x: b.position.x - originX, y: b.position.y - originY }, { offset, length, perpOffset: 0 });
      if (!frame || frame.halfWidth <= 1e-4) continue;

      // Asymmetric band footprint: +wallNorm is the edge's LEFT side (same
      // convention as the 2D cutout rect), so the quad spans −cutRight to
      // +cutLeft about the centerline.
      const cornerAt = (u: number, n: number) => ({
        x: frame.center.x + u * frame.wallDir.x + n * frame.wallNorm.x,
        y: frame.center.y + u * frame.wallDir.y + n * frame.wallNorm.y,
      });
      const ring = [cornerAt(-frame.halfWidth, -cutRight), cornerAt(frame.halfWidth, -cutRight), cornerAt(frame.halfWidth, cutLeft), cornerAt(-frame.halfWidth, cutLeft)];
      const cutter = buildVerticalPrismCutter(ring, lower - OPENING_INFLATE, upper + OPENING_INFLATE);
      if (cutter) cutters.push({ geometry: cutter, tag: tagOf(os.identifier) });
    }
    return cutters;
  }

  /**
   * Gather the 2D data needed to render door/window openings in the flat skin:
   * a footprint cutter rectangle (to break the wall silhouette) and the glyph
   * line segments (the floor-plan door/window symbol) for every opening that
   * belongs to one of this skeleton's rooms. Mirrors {@link buildOpeningCutters}
   * but produces flat 2D geometry instead of 3D prisms.
   */
  private buildOpening2DData(z: number): { cutterRects: { x: number; y: number }[][]; glyphSegments: number[]; selectedGlyphSegments: number[]; openings: OpeningGlyphInput[] } {
    const cutterRects: { x: number; y: number }[][] = [];
    const glyphSegments: number[] = [];
    const selectedGlyphSegments: number[] = [];
    // Per-opening glyph inputs — the DXF export rebuilds each glyph in a local
    // frame from these to emit shared block definitions; ignored by rendering.
    const openings: OpeningGlyphInput[] = [];
    // Rebuild the per-opening glyph segment map (identifier → flat [x,y,z,...])
    // used for click hit-testing of the glyph symbols.
    this._openingGlyphSegments.clear();

    const skeletonRoomIds = new Set<string>();
    for (const e of this.getEdges()) for (const rid of e.roomIds) skeletonRoomIds.add(rid);
    for (const n of this.getNodes()) for (const rid of n.roomIds) skeletonRoomIds.add(rid);
    if (skeletonRoomIds.size === 0) return { cutterRects, glyphSegments, selectedGlyphSegments, openings };

    // The wall sketch renders ONLY the new OPENING_SKETCH glyphs (and their wall
    // cutouts). The legacy floorplan OPENING annotations are no longer drawn here
    // — they belong to the floorplan persona; on floor→sketch conversion each is
    // replicated into an OPENING_SKETCH, which is the single source of truth for
    // the wall sketch's door/window symbols.
    //
    // --- OPENING_SKETCH glyphs (layout persona) ---
    // These are parametric on a wall edge of THIS wall sketch (offset/length/
    // perp-offset along the edge, lower/upper bound vertically). The glyph is
    // built here from the edge geometry — the OPENING_SKETCH scene object only
    // draws a thin selectable marker line.
    for (const os of this.viewer.scene.openingSketches) {
      // Only THIS floor's openings — STRICT match, see buildOpeningCutters:
      // edge ids repeat across floors, so the edge lookup alone would draw
      // other floors' glyphs (and cut their holes) on this floor's walls. A
      // group-less opening must not draw on ANY floor — the old null
      // pass-through leaked its glyph onto every floor's same-named edge,
      // drawn by the SELECTED sketch itself and therefore invisible to the
      // floor-isolation whitelist.
      if (os.groupIdentifier !== this.groupIdentifier) continue;
      let edgeId = '';
      let kind: OpeningKind;
      let doorType: DoorGlyphType;
      let windowType: WindowGlyphType;
      let hingeAtStart: boolean;
      let swingPositive: boolean;
      let rollable: boolean;
      let offset: number;
      let length: number;
      let inset: number;
      let lower: number;
      let upper: number;
      try {
        edgeId = getOpeningSketchWallEdgeIdProperty(os.identifier).value || '';
        kind = getOpeningSketchKindProperty(os.identifier).value;
        doorType = getOpeningSketchDoorTypeProperty(os.identifier).value;
        windowType = getOpeningSketchWindowTypeProperty(os.identifier).value;
        hingeAtStart = getOpeningSketchHingeAtStartProperty(os.identifier).value;
        swingPositive = getOpeningSketchSwingPositiveProperty(os.identifier).value;
        rollable = getOpeningSketchRollableProperty(os.identifier).value;
        offset = getOpeningSketchOffsetProperty(os.identifier).value ?? 0;
        length = getOpeningSketchLengthProperty(os.identifier).value ?? 0;
        inset = getOpeningSketchInsetProperty(os.identifier).value ?? 0;
        lower = getOpeningSketchLowerBoundProperty(os.identifier).value ?? 0;
        // `height` is stored relative to the ground offset; head Z = lower + height.
        upper = lower + (getOpeningSketchHeightProperty(os.identifier).value ?? 0);
      } catch {
        continue;
      }
      if (!edgeId || length <= 1e-4) continue;
      const edge = this.getEdge(edgeId);
      if (!edge) continue; // edge belongs to another wall sketch (or was removed)
      const a = this.nodes.get(edge.startNodeId);
      const b = this.nodes.get(edge.endNodeId);
      if (!a || !b) continue;

      // The CUTOUT frame stays on the wall centerline regardless of the glyph
      // inset — computed first because the measured-face scan below needs it.
      const cutoutFrame = frameFromWallOpeningSketch(
        { x: a.position.x, y: a.position.y },
        { x: b.position.x, y: b.position.y },
        { offset, length, perpOffset: 0 },
        edge.bulge ?? 0,
      );
      if (!cutoutFrame || cutoutFrame.halfWidth <= 1e-4) continue;

      // Wall band sizes: the authored edge sizes in the Wall-sketch view; in
      // the MEASURED view the band is outline ⊖ rooms, whose thickness need
      // not match the authored sizes — measure the real faces at this opening
      // instead (straight edges only; arcs keep the authored sizes), falling
      // back per side when no face is found.
      let leftSize = edge.leftSize ?? 0.1;
      let rightSize = edge.rightSize ?? 0.1;
      if (this.isOriginalRepresentation() && !edgeIsCurved(edge)) {
        // The face scan reaches up to 2m per side; near a corner it can miss
        // the true face and latch onto a distant parallel one, and a cutter
        // that deep crosses the junction and carves the adjacent wall. Cap
        // the accepted face distance at a plausible wall half-thickness —
        // farther hits keep the authored size (an under-cut leaves a bit of
        // fill in the doorway; an over-cut destroys the neighbouring wall).
        const MAX_CUTOUT_FACE_SIZE_M = 0.6;
        const faces = this.measuredWallFaceSizesAt(cutoutFrame);
        if (faces.left !== null && faces.left <= MAX_CUTOUT_FACE_SIZE_M) leftSize = faces.left;
        if (faces.right !== null && faces.right <= MAX_CUTOUT_FACE_SIZE_M) rightSize = faces.right;
      }

      // `inset` is stored as a non-negative distance in from the outer wall face
      // on its side; convert to the signed centerline offset the frame builder
      // wants, using the EFFECTIVE face sizes + the chosen side (so the glyph
      // sits on the band the user actually sees in either representation).
      const perpOffset = signedPerpFromInset(inset, swingPositive, leftSize, rightSize);

      // The GLYPH frame includes perpOffset (the symbol can be drawn off the wall
      // centerline); moving the glyph never moves the hole.
      const glyphFrame = frameFromWallOpeningSketch(
        { x: a.position.x, y: a.position.y },
        { x: b.position.x, y: b.position.y },
        { offset, length, perpOffset },
        edge.bulge ?? 0,
      );
      if (!glyphFrame || glyphFrame.halfWidth <= 1e-4) continue;

      // The wall band is ASYMMETRIC about the centerline (leftSize ≠ rightSize),
      // and the wall fill is drawn offset accordingly. A cutout symmetric about
      // the centerline would miss the thicker side and leave fill behind the
      // opening. Match the wall: depth = leftSize + rightSize, and shift the
      // cutout centre by (leftSize − rightSize)/2 toward the thicker side along
      // the edge's left-perpendicular (= cutoutFrame.wallNorm).
      const wallThickness = leftSize + rightSize;
      const centreShift = (leftSize - rightSize) / 2;
      const shiftedCutoutFrame = {
        ...cutoutFrame,
        center: {
          x: cutoutFrame.center.x + centreShift * cutoutFrame.wallNorm.x,
          y: cutoutFrame.center.y + centreShift * cutoutFrame.wallNorm.y,
        },
      };
      // Small overshoot past each face so the cutout cleanly clears the fill.
      // Curved edges cut with the opening span's arc-band ring (same asymmetric
      // extents as the shifted straight rectangle: −(rightSize+0.04) to
      // +(leftSize+0.04) about the centerline).
      if (edgeIsCurved(edge)) {
        const span = subSpan({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge, offset, offset + length);
        cutterRects.push(tessellateBand(span.start, span.end, span.bulge, leftSize + 0.04, rightSize + 0.04));
      } else {
        const cutDepth = wallThickness + 0.08;
        cutterRects.push(this.openingCutoutRect(shiftedCutoutFrame, cutDepth));
      }

      const glyphInput: OpeningGlyphInput = {
        frame: glyphFrame,
        kind,
        wallThickness,
        hingeAtStart,
        swingPositive,
        doorType,
        windowType,
        rollable,
        height: Math.max(0, upper - lower),
        customParams: this.customOpeningParams(os.identifier, customGlyphTypeOf({ kind, doorType, windowType })),
      };
      openings.push(glyphInput);
      const glyph = buildOpeningGlyph(glyphInput, z);
      const target = os.selected ? selectedGlyphSegments : glyphSegments;
      for (const v of glyph.segments) target.push(v);
      if (glyph.segments.length > 0) this._openingGlyphSegments.set(os.identifier, glyph.segments);
    }

    return { cutterRects, glyphSegments, selectedGlyphSegments, openings };
  }

  /** The param values of an opening drawn with an organisation door / window (empty for built-in symbols). */
  private customOpeningParams(openingId: string, typeId: string | null): Readonly<Record<string, ObjectParamValue>> {
    const type = typeId ? getCustomOpeningType(typeId) : null;
    if (!type || type.spec.params.length === 0) return {};
    try {
      return readParamValues(type.id, specToParamSchema(type.spec), openingId, OPENING_PARAM_SCOPE);
    } catch {
      return {}; // property service not ready: defaults
    }
  }

  /**
   * Subscribe (once per opening) to the reactive properties that change a
   * door/window glyph's appearance but NOT its cutout hole — door type, swing
   * (hinge + direction) and the rollable flag. Any change repaints only the
   * glyph lines via {@link rebuildOpeningGlyphsOnly}, avoiding a full skin
   * rebuild. Idempotent: returns early if already subscribed for this opening.
   */
  /**
   * MEASURED-mode wall thickness at a spot on a wall: the nearest room/outline face
   * line on each side of the wall centerline, restricted to faces roughly
   * parallel to the wall that overlap at least half the opening span. In the
   * Measured representation the band between rooms and outer wall IS the wall
   * — the sketch edge's authored left/right sizes need not match it, so the
   * cutout must measure the real faces (the Wall-sketch view keeps using the
   * authored sizes). Shared by the opening cutouts and the sketch-object
   * placement (wall objects resting on the measured face; free objects
   * displaced out of the measured band). A null side found no face — callers
   * fall back to the edge's authored size there.
   */
  measuredWallFacesAt(frame: { center: { x: number; y: number }; wallDir: { x: number; y: number }; wallNorm: { x: number; y: number }; halfWidth: number }): { left: MeasuredWallFaceHit | null; right: MeasuredWallFaceHit | null } {
    const MAX_FACE_DIST = 2.0;
    const PARALLEL_TOL_SIN = Math.sin((20 * Math.PI) / 180);
    const MIN_OVERLAP_RATIO = 0.5;

    const rings: { x: number; y: number }[][] = [];
    const floorId = this.groupIdentifier;
    if (floorId) {
      for (const room of this.viewer.scene.rooms) {
        if (room.groupIdentifier !== floorId) continue;
        const poly = room.getPolygon();
        if (poly.length >= 3) rings.push(poly.map((p) => ({ x: p.x, y: p.y })));
      }
    }
    for (const component of this.resolveMeasuredOutlineRingsOrDerived() ?? []) {
      rings.push(component.outerRing);
      for (const hole of component.holes) rings.push(hole);
    }

    let left: MeasuredWallFaceHit | null = null; // +wallNorm side
    let right: MeasuredWallFaceHit | null = null; // −wallNorm side
    for (const ring of rings) {
      for (let i = 0; i < ring.length; i++) {
        const p0 = ring[i];
        const p1 = ring[(i + 1) % ring.length];
        const ex = p1.x - p0.x;
        const ey = p1.y - p0.y;
        const elen = Math.hypot(ex, ey);
        if (elen < 1e-9) continue;
        // Roughly parallel to the wall axis — measured faces need NOT be
        // exactly parallel, so the hit carries the face's own line.
        if (Math.abs((ex * frame.wallDir.y - ey * frame.wallDir.x) / elen) > PARALLEL_TOL_SIN) continue;
        // Signed perpendicular distance of the face from the centerline.
        const mx = (p0.x + p1.x) / 2 - frame.center.x;
        const my = (p0.y + p1.y) / 2 - frame.center.y;
        const off = mx * frame.wallNorm.x + my * frame.wallNorm.y;
        if (Math.abs(off) < 1e-4 || Math.abs(off) > MAX_FACE_DIST) continue;
        // The face must actually span (most of) the queried width.
        const s0 = (p0.x - frame.center.x) * frame.wallDir.x + (p0.y - frame.center.y) * frame.wallDir.y;
        const s1 = (p1.x - frame.center.x) * frame.wallDir.x + (p1.y - frame.center.y) * frame.wallDir.y;
        const overlap = Math.min(Math.max(s0, s1), frame.halfWidth) - Math.max(Math.min(s0, s1), -frame.halfWidth);
        if (overlap < MIN_OVERLAP_RATIO * 2 * frame.halfWidth) continue;
        // Face direction oriented ALONG the wall axis (dot > 0) so frames
        // built from it keep the buildFrameFromWall dir convention.
        const flip = ex * frame.wallDir.x + ey * frame.wallDir.y < 0 ? -1 : 1;
        const hit: MeasuredWallFaceHit = { distance: Math.abs(off), point: { x: p0.x, y: p0.y }, dir: { x: (ex / elen) * flip, y: (ey / elen) * flip } };
        if (off > 0) {
          if (left === null || hit.distance < left.distance) left = hit;
        } else if (right === null || hit.distance < right.distance) {
          right = hit;
        }
      }
    }
    return { left, right };
  }

  /** Scalar convenience over {@link measuredWallFacesAt} (opening cutouts —
   *  their rects stay perpendicular to the sketch edge). */
  measuredWallFaceSizesAt(frame: { center: { x: number; y: number }; wallDir: { x: number; y: number }; wallNorm: { x: number; y: number }; halfWidth: number }): { left: number | null; right: number | null } {
    const faces = this.measuredWallFacesAt(frame);
    return { left: faces.left?.distance ?? null, right: faces.right?.distance ?? null };
  }

  /**
   * Effective MEASURED face distance for placement at an along-edge spot, or
   * null when the authored edge face applies (Wall-sketch representation,
   * curved edge, or no measured face found). `sCenter` is the along-edge
   * arc-length of the object's center, `halfWidth` half its along-wall extent
   * (the face must span most of it). Single source for the placement ghost,
   * the persisted-pose resolution and the render frame, so they always agree.
   */
  effectiveMeasuredFaceAt(edgeId: string, side: boolean, sCenter: number, halfWidth: number): number | null {
    return this.effectiveMeasuredFaceLineAt(edgeId, side, sCenter, halfWidth)?.distance ?? null;
  }

  /**
   * Line variant of {@link effectiveMeasuredFaceAt}: the measured face's own
   * LINE (point + along-wall-oriented unit direction). Measured faces are not
   * necessarily parallel to the sketch edge, so flush placement must align to
   * this line — position AND rotation — instead of offsetting the centerline.
   */
  effectiveMeasuredFaceLineAt(edgeId: string, side: boolean, sCenter: number, halfWidth: number): MeasuredWallFaceHit | null {
    if (!this.isOriginalRepresentation()) return null;
    const edge = this.edges.get(edgeId);
    if (!edge || Math.abs(edge.bulge ?? 0) > 1e-6) return null;
    const a = this.nodes.get(edge.startNodeId);
    const b = this.nodes.get(edge.endNodeId);
    if (!a || !b) return null;
    const { point, tangent } = pointAtArcLength({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge, sCenter);
    const faces = this.measuredWallFacesAt({
      center: point,
      wallDir: tangent,
      wallNorm: { x: -tangent.y, y: tangent.x },
      halfWidth: Math.max(halfWidth, 0.05),
    });
    return side ? faces.left : faces.right;
  }

  /**
   * The 2D cutout rectangle for an opening: a quad spanning the opening width
   * along the wall axis and `depth` across the wall normal (centred on the wall
   * plane), ordered as a closed ring.
   */
  private openingCutoutRect(frame: OpeningFrame, depth: number): { x: number; y: number }[] {
    const w = frame.halfWidth;
    const h = depth / 2;
    const corner = (u: number, n: number) => ({
      x: frame.center.x + u * frame.wallDir.x + n * frame.wallNorm.x,
      y: frame.center.y + u * frame.wallDir.y + n * frame.wallNorm.y,
    });
    return [corner(-w, -h), corner(w, -h), corner(w, h), corner(-w, h)];
  }

  private computeEdgeOffsets(edge: SkeletonEdgeData, startNode: SkeletonNodeData, endNode: SkeletonNodeData): EdgeOffsetData {
    const start = new THREE.Vector2(startNode.position.x, startNode.position.y);
    const end = new THREE.Vector2(endNode.position.x, endNode.position.y);
    const direction = new THREE.Vector2().subVectors(end, start).normalize();
    const perpLeft = new THREE.Vector2(-direction.y, direction.x);
    const leftSize = edge.leftSize || 0.1;
    const rightSize = edge.rightSize || 0.1;

    // Travel tangents at the endpoints (chord direction when straight). Cap
    // corners offset perpendicular to the LOCAL tangent so they line up with
    // the tessellated arc band's first/last ring points.
    const bulge = edge.bulge ?? 0;
    const ts = pointAtArcLength({ x: start.x, y: start.y }, { x: end.x, y: end.y }, bulge, 0).tangent;
    const len = arcEdgeLength({ x: start.x, y: start.y }, { x: end.x, y: end.y }, bulge);
    const te = pointAtArcLength({ x: start.x, y: start.y }, { x: end.x, y: end.y }, bulge, len).tangent;
    const startTangent = new THREE.Vector2(ts.x, ts.y);
    const endTangent = new THREE.Vector2(te.x, te.y);
    const startPerp = new THREE.Vector2(-startTangent.y, startTangent.x);
    const endPerp = new THREE.Vector2(-endTangent.y, endTangent.x);

    return {
      edge,
      startNode,
      endNode,
      direction,
      perpLeft,
      startTangent,
      endTangent,
      leftSize,
      rightSize,
      leftStart: start.clone().add(startPerp.clone().multiplyScalar(leftSize)),
      leftEnd: end.clone().add(endPerp.clone().multiplyScalar(leftSize)),
      rightStart: start.clone().add(startPerp.clone().multiplyScalar(-rightSize)),
      rightEnd: end.clone().add(endPerp.clone().multiplyScalar(-rightSize)),
    };
  }

  private computeNodeCorners(nodeId: string, node: SkeletonNodeData, edgeIds: string[], edgeOffsets: Map<string, EdgeOffsetData>, cornerPoints: Map<string, THREE.Vector2>): void {
    if (edgeIds.length === 0) return;

    const nodePos = new THREE.Vector2(node.position.x, node.position.y);

    const edgesAtNode: EdgeAtNode[] = [];
    for (const edgeId of edgeIds) {
      const offsetData = edgeOffsets.get(edgeId);
      if (!offsetData) continue;

      const isStartNode = offsetData.startNode.id === nodeId;
      // Direction out of the node along the edge — the LOCAL tangent, so an
      // arc miters against its tangent line at the junction (correct to first
      // order; the Manifold union absorbs the residual band overlap).
      const outgoingDir = isStartNode ? offsetData.startTangent.clone() : offsetData.endTangent.clone().negate();
      const angle = Math.atan2(outgoingDir.y, outgoingDir.x);

      let cwWallLine: { p1: THREE.Vector2; p2: THREE.Vector2; size: number };
      let ccwWallLine: { p1: THREE.Vector2; p2: THREE.Vector2; size: number };

      // Each wall line is the offset TANGENT line at this node: through the
      // cap corner, along the outgoing tangent. For straight edges this is
      // exactly the full offset segment's line (previous behavior).
      if (isStartNode) {
        ccwWallLine = { p1: offsetData.leftStart, p2: offsetData.leftStart.clone().add(outgoingDir), size: offsetData.leftSize };
        cwWallLine = { p1: offsetData.rightStart, p2: offsetData.rightStart.clone().add(outgoingDir), size: offsetData.rightSize };
      } else {
        ccwWallLine = { p1: offsetData.rightEnd, p2: offsetData.rightEnd.clone().add(outgoingDir), size: offsetData.rightSize };
        cwWallLine = { p1: offsetData.leftEnd, p2: offsetData.leftEnd.clone().add(outgoingDir), size: offsetData.leftSize };
      }

      edgesAtNode.push({ edgeId, offsetData, outgoingDir, isStartNode, angle, cwWallLine, ccwWallLine });
    }

    if (edgesAtNode.length === 0) return;

    edgesAtNode.sort((a, b) => b.angle - a.angle);

    if (edgesAtNode.length === 1) {
      const e = edgesAtNode[0];
      cornerPoints.set(`${nodeId}|${e.edgeId}|cw`, e.cwWallLine.p1.clone());
      cornerPoints.set(`${nodeId}|${e.edgeId}|ccw`, e.ccwWallLine.p1.clone());
      return;
    }

    for (let i = 0; i < edgesAtNode.length; i++) {
      const edgeA = edgesAtNode[i];
      const edgeB = edgesAtNode[(i + 1) % edgesAtNode.length];

      const lineA = edgeA.cwWallLine;
      const lineB = edgeB.ccwWallLine;
      const dotProduct = edgeA.outgoingDir.dot(edgeB.outgoingDir);

      let cornerPoint: THREE.Vector2;
      const intersection = this.lineLineIntersection(lineA.p1, lineA.p2, lineB.p1, lineB.p2);

      if (intersection) {
        const distFromNode = intersection.distanceTo(nodePos);
        const maxDist = Math.max(lineA.size, lineB.size) * 5;

        if (distFromNode <= maxDist) {
          cornerPoint = intersection;
        } else {
          cornerPoint = lineA.p1.clone().add(lineB.p1).multiplyScalar(0.5);
        }
      } else if (dotProduct > this.COLLINEAR_THRESHOLD) {
        const avgSize = (lineA.size + lineB.size) / 2;
        const avgDir = edgeA.outgoingDir.clone().add(edgeB.outgoingDir).normalize();
        const perpCW = new THREE.Vector2(avgDir.y, -avgDir.x);
        cornerPoint = nodePos.clone().add(perpCW.multiplyScalar(avgSize));
      } else {
        cornerPoint = lineA.p1.clone().add(lineB.p1).multiplyScalar(0.5);
      }

      cornerPoints.set(`${nodeId}|${edgeA.edgeId}|cw`, cornerPoint.clone());
      cornerPoints.set(`${nodeId}|${edgeB.edgeId}|ccw`, cornerPoint.clone());
    }
  }

  private lineLineIntersection(p1: THREE.Vector2, p2: THREE.Vector2, p3: THREE.Vector2, p4: THREE.Vector2): THREE.Vector2 | null {
    const d1x = p2.x - p1.x,
      d1y = p2.y - p1.y;
    const d2x = p4.x - p3.x,
      d2y = p4.y - p3.y;
    const cross = d1x * d2y - d1y * d2x;
    if (Math.abs(cross) < 0.0001) return null;
    const dx = p3.x - p1.x,
      dy = p3.y - p1.y;
    const t = (dx * d2y - dy * d2x) / cross;
    return new THREE.Vector2(p1.x + t * d1x, p1.y + t * d1y);
  }

  // ============================================================================
  // WALL OUTLINE COMPUTATION (shared by skinning and SDF)
  // ============================================================================

  /**
   * Compute the exterior wall outline segments from skeleton geometry.
   * These are the actual wall boundaries after applying left/right sizes.
   * Returns an array of line segments [{start: Vector2, end: Vector2}, ...]
   */
  /**
   * Compute mitered wall geometry: edge offsets and corner points.
   * Shared setup for outline segments, wall polygons, and skinning.
   */
  private computeMiteredWallGeometry() {
    const nodes = this.getNodes();
    const edges = this.getEdges(); // wall edges only — outline is never skinned/banded
    if (nodes.length === 0 || edges.length === 0) return null;

    const nodeMap = new Map<string, SkeletonNodeData>();
    for (const node of nodes) nodeMap.set(node.id, node);

    const nodeEdgeIds = new Map<string, string[]>();
    for (const node of nodes) nodeEdgeIds.set(node.id, node.edgeIds || []);

    // Compute edge offsets (wall boundaries)
    const edgeOffsets = new Map<string, EdgeOffsetData>();
    for (const edge of edges) {
      const startNode = nodeMap.get(edge.startNodeId);
      const endNode = nodeMap.get(edge.endNodeId);
      if (!startNode || !endNode) continue;
      edgeOffsets.set(edge.id, this.computeEdgeOffsets(edge, startNode, endNode));
    }

    // Compute corner points for proper mitering at intersections
    const cornerPoints = new Map<string, THREE.Vector2>();
    for (const [nodeId, edgeIds] of nodeEdgeIds) {
      const node = nodeMap.get(nodeId);
      if (!node) continue;
      this.computeNodeCorners(nodeId, node, edgeIds, edgeOffsets, cornerPoints);
    }

    return { nodes, edges, nodeMap, nodeEdgeIds, edgeOffsets, cornerPoints };
  }

  /**
   * Mitered corner points for a single edge — the four endpoints of its
   * rendered wall quad after junction mitering. Returns null if the edge is
   * unknown or its endpoints can't be resolved.
   *
   * Corner naming follows the same {ccw, cw} convention used internally:
   *   startLeft / endLeft  → CCW (left) side of the edge direction
   *   startRight / endRight → CW (right) side
   *
   * Used by the wall-edge handle code so handles can sit at the midpoint of
   * the rendered offset edge instead of a perpendicular projection from the
   * centerline midpoint (which sits inside the corner cut at junctions and
   * looks visually off).
   */
  getMiteredCornersForEdge(edgeId: string): { startLeft: THREE.Vector2; startRight: THREE.Vector2; endLeft: THREE.Vector2; endRight: THREE.Vector2 } | null {
    const geo = this.computeMiteredWallGeometry();
    if (!geo) return null;
    const offset = geo.edgeOffsets.get(edgeId);
    if (!offset) return null;
    const startNodeId = offset.startNode.id;
    const endNodeId = offset.endNode.id;
    return {
      startLeft: geo.cornerPoints.get(`${startNodeId}|${edgeId}|ccw`) ?? offset.leftStart,
      startRight: geo.cornerPoints.get(`${startNodeId}|${edgeId}|cw`) ?? offset.rightStart,
      endLeft: geo.cornerPoints.get(`${endNodeId}|${edgeId}|cw`) ?? offset.leftEnd,
      endRight: geo.cornerPoints.get(`${endNodeId}|${edgeId}|ccw`) ?? offset.rightEnd,
    };
  }

  /**
   * Pick the skeleton edge under a pixel-space cursor. Combines two tests:
   *   1. Polygon hit against the rendered mitered wall quad (the shape the
   *      user actually sees), inflated outward by `polygonPadPx` so the
   *      hover boundary sits a few pixels outside the visible edge.
   *   2. Centerline-strip fallback (`pointToSegmentDistancePx <
   *      centerlineRadiusPx`) for walls that are too thin on screen for
   *      the quad test to have an interior worth hitting.
   *
   * When the cursor is inside multiple walls (overlapping skinning or thick
   * intersecting walls), centerline hits outrank band hits — a cursor on a
   * wall's line always picks that wall, even inside a neighbour's band near
   * a junction — with the closest centerline winning among them; band-only
   * candidates fall back to the closest-midpoint tie-break. Pixel-space
   * throughout — works for ortho and perspective cameras alike and dodges
   * three.js's Raycaster precision issues at UTM world scale.
   *
   * The shared picker for hover/click in tools that act on skeleton edges.
   * Callers stay responsible for higher-priority hits (annotations, nodes,
   * handles) and for translating the picked edge into tool-specific state.
   */
  pickEdgeAtPixel(mousePx: THREE.Vector2, hitTester: PixelHitTester, options: { centerlineRadiusPx?: number; polygonPadPx?: number; thicknessPadPx?: number; excludeOutline?: boolean } = {}): { edgeId: string; distanceToMidpointPx: number; hitKind: 'centerline' | 'band' } | null {
    const centerlineRadiusPx = options.centerlineRadiusPx ?? 12;
    const polygonPadPx = options.polygonPadPx ?? 5;
    // Extra hit slack pushed outward ONLY along the wall's thickness axis (its
    // perpendicular sides), not its length. The side drag handles sit on the
    // offset boundary, so a cursor just off the wall — beside a handle — is
    // still "pointing at this wall". Padding the length too would let a wall
    // grab hovers past its own ends (onto neighbours), so we keep that to
    // `polygonPadPx`. 0 by default → unchanged for non-handle callers.
    const thicknessPadPx = options.thicknessPadPx ?? 0;
    const floorZ = this.getFloorHeight();

    let bestEdgeId: string | null = null;
    let bestMidDist = Infinity;
    let bestCenterlineDist = Infinity;
    let bestHitKind: 'centerline' | 'band' = 'band';
    const outlinePickable = this.shouldShowOutlineSubgraph() && !options.excludeOutline;
    for (const edge of this.getAllEdges()) {
      // Hidden outline edges (Wall-sketch representation / Design tab) must
      // not be pickable either — mirrors isAnnotationPickBlocked for the
      // legacy OUTLINE annotation. Callers placing openings pass
      // `excludeOutline` — a door can't live on a zero-thickness boundary.
      if (edge.role === 'OUTLINE' && !outlinePickable) continue;
      const a = this.getNode(edge.startNodeId);
      const b = this.getNode(edge.endNodeId);
      if (!a || !b) continue;
      const ap = hitTester.projectWorldToPixel(a.position);
      const bp = hitTester.projectWorldToPixel(b.position);
      if (!ap || !bp) continue;

      const aXY = { x: a.position.x, y: a.position.y };
      const bXY = { x: b.position.x, y: b.position.y };

      // Stage 1: the thin skeleton centerline (tessellated polyline for curved
      // edges). Stage 2 (below): the thick wall band. Callers that create a
      // node on an edge require a centerline hit so clicking the wall skin
      // doesn't split it.
      let centerlineDistPx = Infinity;
      const centerline = tessellateEdge(aXY, bXY, edge.bulge);
      let prevPx: THREE.Vector2 | null = null;
      for (const p of centerline) {
        const px = hitTester.projectWorldToPixel({ x: p.x, y: p.y, z: a.position.z });
        if (!px) {
          prevPx = null;
          continue;
        }
        if (prevPx) {
          const d = PixelHitTester.pointToSegmentDistancePx(mousePx, prevPx, px);
          if (d < centerlineDistPx) centerlineDistPx = d;
        }
        prevPx = px;
      }
      const onCenterline = centerlineDistPx < centerlineRadiusPx;
      let hit = onCenterline;

      if (!hit) {
        // Hit-test the wall's offset band (centerline ± each side's thickness),
        // not the mitered junction corners. At sharp / very-asymmetric
        // junctions the mitered corners project far off the wall body,
        // bloating the hover box into empty space — the offset band always
        // hugs the wall (same fix as the side handles). Straight edges yield
        // the familiar 4-corner rectangle; curved edges a tessellated ring.
        const wlen = Math.hypot(bXY.x - aXY.x, bXY.y - aXY.y);
        if (wlen > 1e-9) {
          const ls = edge.leftSize ?? 0.1;
          const rs = edge.rightSize ?? 0.1;
          const ring = tessellateBand(aXY, bXY, edge.bulge, ls, rs);
          const projected: THREE.Vector2[] = [];
          let allProjected = true;
          for (const p of ring) {
            const px = hitTester.projectWorldToPixel({ x: p.x, y: p.y, z: floorZ });
            if (!px) {
              allProjected = false;
              break;
            }
            projected.push(px);
          }
          if (allProjected && projected.length >= 3) {
            let cx = 0;
            let cy = 0;
            for (const p of projected) {
              cx += p.x;
              cy += p.y;
            }
            cx /= projected.length;
            cy /= projected.length;
            const inflate = (p: THREE.Vector2): THREE.Vector2 => {
              const vx = p.x - cx;
              const vy = p.y - cy;
              const len = Math.hypot(vx, vy);
              if (len < 1e-6) return p.clone();
              return new THREE.Vector2(p.x + (vx / len) * polygonPadPx, p.y + (vy / len) * polygonPadPx);
            };
            // Pixel-space unit perpendicular to the wall (its thickness axis):
            // rotate the projected start→end chord direction 90°. Left-side
            // vertices sit on one side, right-side vertices on the other, so
            // push each outward along ±perp by `thicknessPadPx`. The chord
            // perpendicular is an approximation for curved edges but pads are
            // only a few px.
            const dirX = bp.x - ap.x;
            const dirY = bp.y - ap.y;
            const dirLen = Math.hypot(dirX, dirY);
            const perpX = dirLen < 1e-6 ? 0 : -dirY / dirLen;
            const perpY = dirLen < 1e-6 ? 0 : dirX / dirLen;
            const sideSign = (p: THREE.Vector2): number => Math.sign((p.x - cx) * perpX + (p.y - cy) * perpY) || 1;
            const padThickness = (p: THREE.Vector2): THREE.Vector2 => {
              if (thicknessPadPx <= 0) return p;
              const s = sideSign(p);
              return new THREE.Vector2(p.x + perpX * thicknessPadPx * s, p.y + perpY * thicknessPadPx * s);
            };
            const polygon = projected.map((p) => padThickness(inflate(p)));
            if (PixelHitTester.isPointInPolygon2D(mousePx, polygon)) hit = true;
          }
        }
      }

      if (hit) {
        const mid = arcMidpoint(aXY, bXY, edge.bulge);
        const midPx = hitTester.projectWorldToPixel({ x: mid.x, y: mid.y, z: a.position.z });
        const midDist = midPx ? Math.hypot(midPx.x - mousePx.x, midPx.y - mousePx.y) : Math.hypot((ap.x + bp.x) / 2 - mousePx.x, (ap.y + bp.y) / 2 - mousePx.y);
        // Ranking: a cursor ON a wall's centerline always means THAT wall,
        // even when it also sits inside a neighbouring wall's band (the
        // junction area near a corner belongs to both bands, and the
        // neighbour's midpoint can be closer). Centerline hits compete by
        // distance to the line; band-only hits keep the midpoint tie-break.
        const isBetter = onCenterline ? bestHitKind !== 'centerline' || centerlineDistPx < bestCenterlineDist : bestHitKind !== 'centerline' && midDist < bestMidDist;
        if (isBetter) {
          bestMidDist = midDist;
          bestCenterlineDist = centerlineDistPx;
          bestEdgeId = edge.id;
          bestHitKind = onCenterline ? 'centerline' : 'band';
        }
      }
    }

    return bestEdgeId ? { edgeId: bestEdgeId, distanceToMidpointPx: bestMidDist, hitKind: bestHitKind } : null;
  }

  /**
   * Pick the door/window glyph (the floor-plan symbol drawn for an opening)
   * under a pixel-space cursor. Tests the cursor against every opening's glyph
   * line segments (built in {@link buildOpening2DData}) and returns the
   * identifier of the nearest opening within `radiusPx`, or null. Lets a click
   * on the door leaf / swing arc / window symbol select the underlying opening,
   * even where it extends past the opening's drawn rectangle.
   */
  pickOpeningGlyphAtPixel(mousePx: THREE.Vector2, hitTester: PixelHitTester, radiusPx = 8): { openingId: string; distancePx: number } | null {
    let bestId: string | null = null;
    let bestDist = radiusPx;
    for (const [openingId, segs] of this._openingGlyphSegments) {
      for (let i = 0; i + 5 < segs.length; i += 6) {
        const a = hitTester.projectWorldToPixel({ x: segs[i], y: segs[i + 1], z: segs[i + 2] });
        const b = hitTester.projectWorldToPixel({ x: segs[i + 3], y: segs[i + 4], z: segs[i + 5] });
        if (!a || !b) continue;
        const d = PixelHitTester.pointToSegmentDistancePx(mousePx, a, b);
        if (d < bestDist) {
          bestDist = d;
          bestId = openingId;
        }
      }
    }
    return bestId ? { openingId: bestId, distancePx: bestDist } : null;
  }

  /**
   * Pick the skeleton node under a pixel-space cursor. Returns the nearest
   * node within `radiusPx`, or null. Pixel-space distance for the same
   * reasons `pickEdgeAtPixel` documents — Raycaster precision falls off at
   * UTM scale + deep zoom.
   */
  pickNodeAtPixel(mousePx: THREE.Vector2, hitTester: PixelHitTester, radiusPx = 14): { nodeId: string; distancePx: number } | null {
    let bestNodeId: string | null = null;
    let bestDist = radiusPx;
    for (const node of this.getNodes()) {
      const px = hitTester.projectWorldToPixel(node.position);
      if (!px) continue;
      const d = px.distanceTo(mousePx);
      if (d < bestDist) {
        bestDist = d;
        bestNodeId = node.id;
      }
    }
    return bestNodeId ? { nodeId: bestNodeId, distancePx: bestDist } : null;
  }

  computeWallOutlineSegments(): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> {
    const geo = this.computeMiteredWallGeometry();
    if (!geo) return [];
    const { edges, nodeEdgeIds, edgeOffsets, cornerPoints } = geo;

    // Collect outline segments - track counts to find exterior edges
    const outlineSegments = new Map<string, { p1: THREE.Vector2; p2: THREE.Vector2; count: number }>();

    const addOutlineSegment = (p1: THREE.Vector2, p2: THREE.Vector2) => {
      const key1 = `${p1.x.toFixed(6)},${p1.y.toFixed(6)}`;
      const key2 = `${p2.x.toFixed(6)},${p2.y.toFixed(6)}`;
      const segmentKey = key1 < key2 ? `${key1}|${key2}` : `${key2}|${key1}`;
      const existing = outlineSegments.get(segmentKey);
      if (existing) {
        existing.count++;
      } else {
        outlineSegments.set(segmentKey, { p1: p1.clone(), p2: p2.clone(), count: 1 });
      }
    };

    // Collect wall polygon outline segments for each edge
    for (const edge of edges) {
      const offsetData = edgeOffsets.get(edge.id);
      if (!offsetData) continue;

      const edgeId = offsetData.edge.id;
      const startNodeId = offsetData.startNode.id;
      const endNodeId = offsetData.endNode.id;

      const startLeftCorner = cornerPoints.get(`${startNodeId}|${edgeId}|ccw`) || offsetData.leftStart;
      const startRightCorner = cornerPoints.get(`${startNodeId}|${edgeId}|cw`) || offsetData.rightStart;
      const endLeftCorner = cornerPoints.get(`${endNodeId}|${edgeId}|cw`) || offsetData.leftEnd;
      const endRightCorner = cornerPoints.get(`${endNodeId}|${edgeId}|ccw`) || offsetData.rightEnd;

      // Add wall polygon edges. Curved walls emit their sides as tessellated
      // offset polylines (mitered corners at the ends, arc points between).
      addOutlineSegment(startLeftCorner, startRightCorner);
      if (edgeIsCurved(edge)) {
        const a = { x: offsetData.startNode.position.x, y: offsetData.startNode.position.y };
        const b = { x: offsetData.endNode.position.x, y: offsetData.endNode.position.y };
        const chainSide = (offsetPts: Array<{ x: number; y: number }>, first: THREE.Vector2, last: THREE.Vector2) => {
          const pts = offsetPts.map((p) => new THREE.Vector2(p.x, p.y));
          if (pts.length >= 2) {
            pts[0] = first.clone();
            pts[pts.length - 1] = last.clone();
          }
          for (let i = 0; i + 1 < pts.length; i++) addOutlineSegment(pts[i], pts[i + 1]);
        };
        chainSide(tessellateOffset(a, b, edge.bulge, -offsetData.rightSize), startRightCorner, endRightCorner);
        chainSide(tessellateOffset(a, b, edge.bulge, offsetData.leftSize), startLeftCorner, endLeftCorner);
      } else {
        addOutlineSegment(startRightCorner, endRightCorner);
        addOutlineSegment(endLeftCorner, startLeftCorner);
      }
      addOutlineSegment(endRightCorner, endLeftCorner);
    }

    // Collect junction fill segments for nodes with more than 2 edges
    for (const [nodeId, edgeIds] of nodeEdgeIds) {
      if (edgeIds.length > 2) {
        // Get edges at this node sorted by angle
        const edgesAtNode: { edgeId: string; angle: number }[] = [];
        for (const edgeId of edgeIds) {
          const offsetData = edgeOffsets.get(edgeId);
          if (!offsetData) continue;

          const isStartNode = offsetData.startNode.id === nodeId;
          // Local tangent (matches computeNodeCorners' angular ordering).
          const outgoingDir = isStartNode ? offsetData.startTangent.clone() : offsetData.endTangent.clone().negate();
          const angle = Math.atan2(outgoingDir.y, outgoingDir.x);

          edgesAtNode.push({ edgeId, angle });
        }

        edgesAtNode.sort((a, b) => b.angle - a.angle);

        // Add junction fill polygon edges
        const junctionPoints: THREE.Vector2[] = [];
        for (let i = 0; i < edgesAtNode.length; i++) {
          const cwCorner = cornerPoints.get(`${nodeId}|${edgesAtNode[i].edgeId}|cw`);
          if (cwCorner) junctionPoints.push(cwCorner);
        }

        for (let i = 0; i < junctionPoints.length; i++) {
          const p1 = junctionPoints[i];
          const p2 = junctionPoints[(i + 1) % junctionPoints.length];
          addOutlineSegment(p1, p2);
        }
      }
    }

    // Extract exterior segments (those that appear only once)
    const result: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];
    for (const segment of outlineSegments.values()) {
      if (segment.count === 1) {
        result.push({ start: segment.p1, end: segment.p2 });
      }
    }

    return result;
  }

  /**
   * Get wall outline as ordered closed loops of plain coordinate objects.
   * Returns multiple loops: outer perimeter, inner room cavities, disconnected islands.
   */
  getWallOutlineLoops(): Array<Array<{ x: number; y: number; z: number }>> {
    const segments = this.computeWallOutlineSegments();
    if (segments.length === 0) return [];

    // Build adjacency map: vertex key -> connected vertex keys
    const PRECISION = 6;
    const vertexKey = (v: THREE.Vector2): string => `${v.x.toFixed(PRECISION)},${v.y.toFixed(PRECISION)}`;
    const makeEdgeKey = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);

    const adjacency = new Map<string, Set<string>>();
    const pointMap = new Map<string, THREE.Vector2>();

    for (const seg of segments) {
      const keyA = vertexKey(seg.start);
      const keyB = vertexKey(seg.end);
      if (keyA === keyB) continue; // Skip degenerate zero-length segments

      pointMap.set(keyA, seg.start);
      pointMap.set(keyB, seg.end);

      if (!adjacency.has(keyA)) adjacency.set(keyA, new Set());
      if (!adjacency.has(keyB)) adjacency.set(keyB, new Set());
      adjacency.get(keyA)?.add(keyB);
      adjacency.get(keyB)?.add(keyA);
    }

    // Chain segments into ordered closed loops using angle-based traversal
    const usedEdges = new Set<string>();
    const loops: Array<Array<{ x: number; y: number; z: number }>> = [];
    const z = this.getFloorHeight();

    for (const [startKey] of adjacency) {
      const startNeighbors = adjacency.get(startKey);
      if (!startNeighbors || startNeighbors.size === 0) continue;

      for (const firstNeighbor of startNeighbors) {
        const firstEdge = makeEdgeKey(startKey, firstNeighbor);
        if (usedEdges.has(firstEdge)) continue;

        const loop: Array<{ x: number; y: number; z: number }> = [];
        let currentKey = startKey;
        let nextKey: string | null = firstNeighbor;

        while (nextKey !== null) {
          const ek = makeEdgeKey(currentKey, nextKey);
          if (usedEdges.has(ek)) break;
          usedEdges.add(ek);

          const pt = pointMap.get(currentKey);
          if (!pt) break;
          loop.push({ x: pt.x, y: pt.y, z });

          const prevKey = currentKey;
          currentKey = nextKey;

          if (currentKey === startKey) break; // Loop closed

          // Pick next neighbor by most clockwise turn (angle-based)
          const neighbors = adjacency.get(currentKey);
          if (!neighbors || neighbors.size === 0) break;

          const prevPt = pointMap.get(prevKey);
          const currPt = pointMap.get(currentKey);
          if (!prevPt || !currPt) break;
          const incomingAngle = Math.atan2(prevPt.y - currPt.y, prevPt.x - currPt.x);

          let bestKey: string | null = null;
          let bestAngleDiff = -Infinity;

          for (const neighbor of neighbors) {
            if (neighbor === prevKey) continue;
            const nek = makeEdgeKey(currentKey, neighbor);
            if (usedEdges.has(nek) && neighbor !== startKey) continue;

            const neighborPt = pointMap.get(neighbor);
            if (!neighborPt) continue;
            const outgoingAngle = Math.atan2(neighborPt.y - currPt.y, neighborPt.x - currPt.x);

            // Angle difference: pick the most clockwise turn
            let angleDiff = incomingAngle - outgoingAngle;
            while (angleDiff <= -Math.PI) angleDiff += 2 * Math.PI;
            while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;

            if (angleDiff > bestAngleDiff) {
              bestAngleDiff = angleDiff;
              bestKey = neighbor;
            }
          }

          nextKey = bestKey;
        }

        if (loop.length >= 3) {
          loops.push(loop);
        }
      }
    }

    return loops;
  }

  /**
   * Get individual wall polygons (mitered quads + junction fills) for IFC export.
   * Each polygon is a simple, non-self-intersecting face that tiles gap-free.
   * Returns array of 2D polygons with z = floorHeight.
   */
  getWallPolygons(): Array<Array<{ x: number; y: number; z: number }>> {
    const geo = this.computeMiteredWallGeometry();
    if (!geo) return [];
    const { edges, nodeEdgeIds, edgeOffsets, cornerPoints } = geo;

    const z = this.getFloorHeight();
    const polygons: Array<Array<{ x: number; y: number; z: number }>> = [];

    // Mitered wall quads (one per edge)
    for (const edge of edges) {
      const offsetData = edgeOffsets.get(edge.id);
      if (!offsetData) continue;

      const edgeId = offsetData.edge.id;
      const startNodeId = offsetData.startNode.id;
      const endNodeId = offsetData.endNode.id;

      const startLeft = cornerPoints.get(`${startNodeId}|${edgeId}|ccw`) || offsetData.leftStart;
      const startRight = cornerPoints.get(`${startNodeId}|${edgeId}|cw`) || offsetData.rightStart;
      const endLeft = cornerPoints.get(`${endNodeId}|${edgeId}|cw`) || offsetData.leftEnd;
      const endRight = cornerPoints.get(`${endNodeId}|${edgeId}|ccw`) || offsetData.rightEnd;

      if (edgeIsCurved(edge)) {
        // Curved wall face: mitered end corners + tessellated arc sides,
        // walked in the same winding as the straight quad below.
        const a = { x: offsetData.startNode.position.x, y: offsetData.startNode.position.y };
        const b = { x: offsetData.endNode.position.x, y: offsetData.endNode.position.y };
        const rightPts = tessellateOffset(a, b, edge.bulge, -offsetData.rightSize);
        const leftPts = tessellateOffset(a, b, edge.bulge, offsetData.leftSize);
        const ring: Array<{ x: number; y: number; z: number }> = [{ x: startLeft.x, y: startLeft.y, z }, { x: startRight.x, y: startRight.y, z }];
        for (let i = 1; i + 1 < rightPts.length; i++) ring.push({ x: rightPts[i].x, y: rightPts[i].y, z });
        ring.push({ x: endRight.x, y: endRight.y, z }, { x: endLeft.x, y: endLeft.y, z });
        for (let i = leftPts.length - 2; i >= 1; i--) ring.push({ x: leftPts[i].x, y: leftPts[i].y, z });
        polygons.push(ring);
      } else {
        polygons.push([
          { x: startLeft.x, y: startLeft.y, z },
          { x: startRight.x, y: startRight.y, z },
          { x: endRight.x, y: endRight.y, z },
          { x: endLeft.x, y: endLeft.y, z },
        ]);
      }
    }

    // Junction fill polygons (nodes with >2 edges)
    for (const [nodeId, edgeIds] of nodeEdgeIds) {
      if (edgeIds.length <= 2) continue;

      const edgesAtNode: { edgeId: string; angle: number }[] = [];
      for (const edgeId of edgeIds) {
        const offsetData = edgeOffsets.get(edgeId);
        if (!offsetData) continue;
        const isStartNode = offsetData.startNode.id === nodeId;
        // Local tangent (matches computeNodeCorners' angular ordering).
        const outgoingDir = isStartNode ? offsetData.startTangent.clone() : offsetData.endTangent.clone().negate();
        edgesAtNode.push({ edgeId, angle: Math.atan2(outgoingDir.y, outgoingDir.x) });
      }

      edgesAtNode.sort((a, b) => b.angle - a.angle);

      const junctionPoints: THREE.Vector2[] = [];
      for (const e of edgesAtNode) {
        const corner = cornerPoints.get(`${nodeId}|${e.edgeId}|cw`);
        if (corner) junctionPoints.push(corner);
      }

      if (junctionPoints.length >= 3) {
        polygons.push(junctionPoints.map((p) => ({ x: p.x, y: p.y, z })));
      }
    }

    return polygons;
  }

  // ============================================================================
  // SDF (SIGNED DISTANCE FIELD) METHODS FOR POINT CLOUD COLORING
  // ============================================================================

  /**
   * Schedule SDF regeneration (debounced to avoid excessive recomputation)
   */
  private scheduleSDFRegeneration(): void {
    if (this._sdfRegenerationPending) return;
    this._sdfRegenerationPending = true;
    requestAnimationFrame(() => {
      this._sdfRegenerationPending = false;
      this.regenerateSDF();
    });
  }

  /**
   * Regenerate edge texture from current skeleton wall outlines
   */
  private regenerateSDF(): void {
    // SDF is needed if either gradient visualization OR clipping is enabled
    if (!this._sdfEnabled && !this._sdfClipEnabled) return;

    // Get wall outline segments (exterior boundaries of skinned walls)
    const outlineSegments = this.computeWallOutlineSegments();
    if (outlineSegments.length === 0) return;

    // Create edge data texture: each pixel = one segment (startX, startY, endX, endY)
    const segmentCount = outlineSegments.length;
    const textureData = new Float32Array(segmentCount * 4);

    for (let i = 0; i < segmentCount; i++) {
      const segment = outlineSegments[i];
      textureData[i * 4 + 0] = segment.start.x; // R = startX
      textureData[i * 4 + 1] = segment.start.y; // G = startY
      textureData[i * 4 + 2] = segment.end.x; // B = endX
      textureData[i * 4 + 3] = segment.end.y; // A = endY
    }

    // Dispose old texture if exists
    if (this._edgeTexture) {
      this._edgeTexture.dispose();
    }

    // Create 1D texture (width = segmentCount, height = 1)
    this._edgeTexture = new THREE.DataTexture(textureData, segmentCount, 1, THREE.RGBAFormat, THREE.FloatType);
    this._edgeTexture.internalFormat = 'RGBA32F';
    this._edgeTexture.minFilter = THREE.NearestFilter;
    this._edgeTexture.magFilter = THREE.NearestFilter;
    this._edgeTexture.wrapS = THREE.ClampToEdgeWrapping;
    this._edgeTexture.wrapT = THREE.ClampToEdgeWrapping;
    this._edgeTexture.needsUpdate = true;
    this._edgeCount = segmentCount;

    // Update point clouds with edge data
    this.updateSDFForPointClouds();
  }

  /**
   * Map gradient name to shader gradient index
   */
  private getGradientIndex(gradientName: string): number {
    const gradientMap: Record<string, number> = {
      SPECTRAL: 0,
      PLASMA: 1,
      YELLOW_GREEN: 2,
      VIRIDIS: 3,
      INFERNO: 4,
      GRAYSCALE: 5,
      TURBO: 6,
      RAINBOW: 7,
      CONTOUR: 8,
    };
    return gradientMap[gradientName] ?? 0;
  }

  /**
   * Update all point cloud materials with current edge distance settings
   */
  private updateSDFForPointClouds(): void {
    // SDF shader is needed if either gradient visualization OR clipping is enabled
    const needsSDF = this._sdfEnabled || this._sdfClipEnabled;
    if (!needsSDF || !this._edgeTexture) {
      this.clearSDFFromPointClouds();
      return;
    }

    const pointclouds = this.viewer?.scene?.pointclouds || [];
    const gradientIndex = this._sdfEnabled ? this.getGradientIndex(this._sdfGradient) : -1;

    // Map clip method string to ClipTask enum values
    // Only apply clip method if clipping is enabled, otherwise use NONE (0)
    const clipMethodMap: Record<string, number> = {
      SHOW_INSIDE: 2, // Isolate
      SHOW_OUTSIDE: 3, // Hide
    };
    const clipMethodValue = this._sdfClipEnabled ? (clipMethodMap[this._sdfClipMethod] ?? 0) : 0;

    // The shader's height gate is SYMMETRIC around the passed height
    // (`|z − h| ≤ tolerance`), so pass the band's MIDPOINT with half the
    // floor→ceiling range — the effects then cover exactly floor→ceiling
    // instead of also reaching a full floor-height below the floor.
    const bandHalf = this.getSdfHeightTolerance() / 2;
    const bandCenter = this.getFloorHeight() + bandHalf;

    for (const pc of pointclouds) {
      const material = pc.material;
      if (material && material.setWallSketchEdges) {
        // IMPORTANT: Enable first to trigger shader recompile,
        // then set uniforms so they're applied to the new shader program
        material.wallSketchSDFEnabled = true;
        material.setWallSketchEdges(this._edgeTexture, this._edgeCount, this._sdfMaxDistance, gradientIndex, bandCenter, bandHalf, clipMethodValue, this._sdfClipTolerance);
      }
    }
  }

  /**
   * Get the current edge texture (for debug visualization)
   */
  getEdgeTexture(): THREE.DataTexture | null {
    return this._edgeTexture;
  }

  /**
   * Get the current edge count
   */
  getEdgeCount(): number {
    return this._edgeCount;
  }

  /**
   * Check if SDF is currently enabled
   */
  isSDFEnabled(): boolean {
    return this._sdfEnabled;
  }

  /**
   * Clear SDF from all point cloud materials
   */
  private clearSDFFromPointClouds(): void {
    const pointclouds = this.viewer?.scene?.pointclouds || [];

    for (const pc of pointclouds) {
      const material = pc.material;
      if (material && material.clearWallSketchSDF) {
        material.clearWallSketchSDF();
      }
    }

    // Dispose old edge texture
    if (this._edgeTexture) {
      this._edgeTexture.dispose();
      this._edgeTexture = null;
      this._edgeCount = 0;
    }
  }

  /**
   * Dispose and clean up
   */
  dispose(): void {
    // Cancel any pending Manifold-ready rebuild.
    this._manifoldReadyUnsub?.();
    this._manifoldReadyUnsub = null;

    // Unsubscribe from reactive properties
    this._overlayVisibleSubscription?.unsubscribe();
    this._skinning3DSubscription?.unsubscribe();
    this._skinning2DColorSubscription?.unsubscribe();
    this._skinning2DOutlineColorSubscription?.unsubscribe();
    this._skinning3DOpacitySubscription?.unsubscribe();
    this._skinning3DSubscription = null;
    this._skinning2DColorSubscription = null;
    this._skinning2DOutlineColorSubscription = null;
    this._skinning3DOpacitySubscription = null;

    // Unsubscribe from SDF properties
    this._sdfEnabledSubscription?.unsubscribe();
    this._sdfGradientSubscription?.unsubscribe();
    this._sdfMaxDistanceSubscription?.unsubscribe();
    this._sdfClipEnabledSubscription?.unsubscribe();
    this._sdfClipMethodSubscription?.unsubscribe();
    this._sdfClipToleranceSubscription?.unsubscribe();
    this._wallSourceSubscription?.unsubscribe();
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._sdfEnabledSubscription = null;
    this._sdfGradientSubscription = null;
    this._sdfMaxDistanceSubscription = null;
    this._sdfClipEnabledSubscription = null;
    this._sdfClipMethodSubscription = null;
    this._sdfClipToleranceSubscription = null;
    this._colorSubscription = null;
    this._visibleSubscription = null;

    // Clear SDF from point clouds and dispose texture
    this.clearSDFFromPointClouds();

    // Remove point cloud added listener
    this._customOpeningTypesUnsub?.();
    this._customOpeningTypesUnsub = null;
    if (this._onSelectionChanged) {
      this.viewer.scene.removeEventListener('objects_selection_changed', this._onSelectionChanged);
      this._onSelectionChanged = null;
    }
    if (this._onPointCloudAdded) {
      this.viewer.scene.removeEventListener('pointcloud_added', this._onPointCloudAdded);
      this._onPointCloudAdded = null;
    }

    // Dispose skinning visualization and remove from floorplanTool.scene
    this.clearSkinningVisualization();
    if (this.skinningGroup) {
      const floorplanScene = this.viewer.floorplanTool?.scene;
      if (floorplanScene && floorplanScene.children.includes(this.skinningGroup)) {
        floorplanScene.remove(this.skinningGroup);
      }
      this.skinningGroup = null;
    }

    // Dispose node markers
    for (const marker of this.spheres) {
      marker.dispose();
      this.nodeGroup.remove(marker);
    }
    this.spheres = [];
    this.nodeMarkerMap.clear();

    // Dispose line geometry and materials
    this.lineGeometry.dispose();
    this.lineMaterial.dispose();
    this.backgroundLineMaterial.dispose();
    this.errorLineGeometry.dispose();
    this.errorLineMaterial.dispose();

    // Remove lines from this object
    this.remove(this.line);
    this.remove(this.backgroundLine);
    this.remove(this.errorLine);

    // Remove groups
    this.remove(this.nodeGroup);
    this.remove(this.edgeGroup);

    // Remove the wall-region overlay (which lives in viewer.scene.scene, not here)
    if (this.wallRegionGeometry) {
      this.wallRegionGeometry.dispose();
      this.wallRegionGeometry = null;
    }
    if (this.wallRegionMaterial) {
      this.wallRegionMaterial.dispose();
      this.wallRegionMaterial = null;
    }
    if (this.wallRegionGroup) {
      this.viewer.scene.scene.remove(this.wallRegionGroup);
      this.wallRegionGroup = null;
    }
    this.wallRegionLine = null;
    this._wallRegionsSubscription?.unsubscribe();
    this._wallRegionsSubscription = null;

    // Dispose the selection's edge overlay (a LineSegments2 that lives in
    // viewer.scene.scene, not under this object). The selection is owned by this
    // skeleton, so its GPU resources leak unless we tear them down here.
    this._selection?.dispose();
    this._selection = null;
    this._selectionSubscribers.clear();
    this._dataSubscribers.clear();
  }
}
